// 제작 현황 보드 (/production) — 에이전트 0.9.2 공정 대시보드의 웹 이식 (2026-08-05 대표 확정)
// 디자인 정본: dashboard_preview_1.html — 치과 모드 기공소 배지.
// 기공소 표시 3단계(2026-10-12 대표 확정 — 덴클 앱 1.3.0 §10 "권고대로"): 의뢰서 확인 / 디자인 완료 / 포장 완료 (+ 단계 아님 '의뢰서 접수' 칸)
//   판정 정본 = utils/shared/clinic-stage.js stage3Of (정본 dentcluster-agent/shared — 사본 직접 수정 금지). 계약 docs/계약_공정3단계_2026-10-12.md
//   치과 모드(mode 'clinic', 메뉴 미노출·URL 진입만)도 같은 3단계(2026-10-12 대표 "권고대로" — 치과 화면도 3단계). 칸·목록 진행 표시·캘린더 배지 공통.
// (구) 표시 4단계(2026-09-29 대표 확정 "버튼 0개"): 의뢰서 접수 / 확인 / 디자인완료 / 밀링완료 — 판정 정본은 utils/clinicStage.js(stageColOf, 열 키 intake/confirm/design/mill)
//   완료는 서버가 납기일 자정에 자동 처리(stageSource 'auto_due')하고, 완료(done)·구 shipping·status completed는 보드에서 내려간다(캘린더 [완료 포함] 시 회색).
// 데이터: workOrders.processStage('received'|'scanning'|'designing'|'milling'|'shipping'|'done') + designDone — 저장값은 그대로(에이전트·rules·CF 무변경)
// 수동 이동은 rules hasOnly와 일치하는 필드만 update (processStage/designDone/status/stageUpdatedAt/stageSource/stageHistory)
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import {
    collection, query, where, orderBy, onSnapshot, limit,
    doc, getDoc, getDocs, updateDoc, serverTimestamp, arrayUnion
} from 'firebase/firestore';
import { db } from '../firebase/config';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { displayPatientName } from '../utils/patientName';
import { remakeLabelOf, remakeBadgeColors } from '../utils/remakeKind';
import { isMissingStmtFolder } from '../utils/agentFolders';
import { joinHomeCases, splitCaseLabel, deviceLabelOf, homeReturnLabel, HOME_RETURN_LOST_HINT } from '../utils/homeWorkView';
import { getFeatures, isClinicScanIntakeOn } from '../utils/features';
import CaseCheckBoard from './CaseCheckBoard';
import ProductionCalendar from './ProductionCalendar';   // 캘린더 보기 (2026-09-29 대표 확정) — 표시 계층만, 쓰기는 이 파일 핸들러 재사용
import StlViewer from './StlViewer';   // 캘린더 [3D 디자인 확인] (2026-09-29) — OrderList.openDesignPreview와 같은 entries 규약
import { buildCheckPatch, getStallInfo, STALL_STAGES, STALL_VISIT_SLACK_DAYS } from '../utils/caseChecks';
import { stage3Of, isClosedStage, clinicStageOf, STAGE3_LABELS } from '../utils/shared/clinic-stage';   // 공정 3단계 — 기공소·치과 모드 공통 (2026-10-12)

// ── 팔레트 (2026-08-18 디자인 통일 — 대표 확정: 파랑+웜그레이 → 보라(#6366f1)+슬레이트 계열) ──
const C = {
    plane: '#f8fafc',
    surface: '#ffffff',
    textPrimary: '#0f172a',
    textSecondary: '#334155',
    muted: '#64748b',
    border: '#e2e8f0',
    gridline: '#e2e8f0',
    critical: '#d03b3b',
    good: '#0ca30c',
    cat: ['#6366f1', '#eb6834', '#1baf7a', '#eda100'],   // cat-1~4 순환 (치과 모드 기공소 구분색 — 서로 달라야 함)
};

// 데이터 필드(processStage) 값 — 에이전트·rules와 공유하는 원본 모델 (변경 금지)
const STAGE_KEYS = ['received', 'scanning', 'designing', 'milling', 'shipping'];
const DONE = 'done';

// (구) 표시 열 4단계 COLUMNS(2026-09-29 — 의뢰서 접수 / 확인 / 디자인완료 / 밀링완료)는 2026-10-12 치과 모드까지 3단계로 바뀌어 삭제했다.
//   아래 설명은 열 키 이력 참고용.
//   · 열 키(intake/confirm/design/mill)는 utils/clinicStage.js·functions/lib/shared 와 공유하는 고정값.
//     확인 = received·scanning·designing(designDone!==true) / 디자인완료 = designing+designDone===true / 밀링완료 = milling.
//   · 종결(shipping·done·status completed)은 stageColOf가 null → 보드에서 내린다(캘린더 [완료 포함] 시 회색). 완료는 서버가 납기 자정에 자동 처리.
//   · 'intake'(의뢰서 접수) 칸은 processStage 없음 = 기공소가 아직 안 본 의뢰서. 아래 receivedOrders(기공소)·clinicReceivedRows(치과)가
//     따로 그리므로 STAGE_COLUMNS(확인/디자인완료/밀링완료)만 공정 열 map에 쓴다.
// 표시 열 — 3단계 (2026-10-12, 기공소·치과 모드 공통). 'intake'(의뢰서 접수)는 단계가 아니라 아직 안 본 의뢰서 칸(칩 없음 = stage3Of key null).
//   밀링 신호(processStage milling·checks.mill)는 데이터 그대로, 표시는 「디자인 완료」로 묶인다(stage3Of). 'packed' = 포장 완료 체크 등.
const LAB_COLUMNS = [
    ['intake', '의뢰서 접수'],
    ['confirm', STAGE3_LABELS.confirmed],
    ['design', STAGE3_LABELS.designDone],
    ['packed', STAGE3_LABELS.packed],
];
// stage3Of key → 기공소 표시 열 키 (key null = 접수 칸)
const STAGE3_COL = { confirmed: 'confirm', designDone: 'design', packed: 'packed' };
// 기공소 표시 열 — 종결(shipping/done·status completed, 서버 자동 완료 포함)은 종전처럼 보드에서 내린다(null)
const labColOf = (o) => (isClosedStage(o) ? null : (STAGE3_COL[stage3Of(o).key] || null));
// 가공·3D 체크(구 밀링 열 체크 승계)가 붙는 열 — 디자인완료·밀링완료 두 칸
const CHECK_COLS = ['design', 'mill'];
// (구) rawColOf(치과 모드 4열 판정)는 2026-10-12 삭제 — 치과 모드도 labColOf(stage3Of). null = 접수(칩 없음)·종결 = 공정 열에서 내림.
// 표시 열 이동 시 실제로 쓸 필드 (designDone은 rules hasOnly에 포함됨)
// status 동기화: 완료(status completed)는 서버가 납기일 자정에 자동 처리한다(2026-09-29 — 수동 4번째 열·일괄완료 버튼 제거).
//   앞 단계로 되돌리면 다시 in_progress로.
// 확인 = designing+designDone:false("확인=디자인 시작까지", 대표 결정 1) · 디자인완료 = designing+designDone:true · 밀링완료 = milling
//   (firestore.rules workOrders 공정 분기 processStage 허용 목록에 'milling' 포함 확인 — 2026-09-29)
// 되돌리기(디자인완료→확인)에 'received'를 쓰면 CF가 '의뢰서 접수' 유령 푸시를 보낸다(검토 A-2) — designing+designDone:false로만 되돌린다.
// 접수→확인 수동 이동(moveIntakeToConfirm)은 열람·인쇄 자동 기록과 같은 'received'를 그대로 쓴다(그 경로는 접수 푸시가 정상).
// 확인→접수(processStage 제거)는 rules(processStage in [...])상 불가라 버튼 없음. 필드는 rules hasOnly 집합 안(processStage/designDone/status).
const writeForCol = {
    confirm: { processStage: 'designing', designDone: false, status: 'in_progress' },   // 되돌리기: 디자인완료→확인
    design: { processStage: 'designing', designDone: true, status: 'in_progress' },
    mill: { processStage: 'milling', status: 'in_progress' },                            // 되돌리기(밀링완료→디자인완료)는 design 값으로
};
// 단계 이동 버튼(기공소 전용 — 치과 모드는 버튼 없음): 2026-10-12 3단계 — 의뢰서 확인 ⇄ 디자인 완료만.
//   (구 '디자인완료→밀링완료' 이동 버튼은 밀링완료 열이 없어져 정리. writeForCol.mill 값 규칙은 저장 규칙이라 그대로 둔다)
//   포장 완료는 공정 체크(QR·[✓ 공정 체크] 보기)의 포장 체크로만 기록된다 — 보드에 쓰기 경로를 새로 만들지 않았다.
const nextColOf = { confirm: 'design' };
const prevColOf = { design: 'confirm' };
// 되돌리기 차단 (2026-10-12 검토 P2-1): 밀링 신호(processStage milling·checks.mill/milling/implantMill·millChecked)로 「디자인 완료」가 된 카드는
//   writeForCol.confirm(designing+designDone:false)을 써도 stage3Of가 다시 디자인 완료로 판정하거나(체크 잔존) processStage milling만 지운다.
//   그래서 버튼을 숨기고 안내 문구만 보인다. 밀링 기록 자체를 지우는 경로는 만들지 않는다.
const REVERT_BLOCKED_MSG = '밀링 기록이 있어 되돌릴 수 없습니다';
const checkMapDone = (o, key) => {
    const c = o && o.checks && typeof o.checks === 'object' ? o.checks[key] : undefined;
    return c === true || !!(c && typeof c === 'object' && c.done === true);
};
const AGENT_ALIVE_MS = 15 * 60 * 1000;   // 하트비트 5분 주기 × 3회 유예 (에이전트 치과 모드와 동일)

// ── 조회 창 (2026-08-09 전체회의 P0-1·P0-2) ──────────────────────────────
// 종전: 두 쿼리 모두 limit(200). workOrders는 에이전트 자동수집(agent_auto)이 창을 잠식하고
// (실측 아이반랩 2,839건 중 agent_auto 1,246건 — 최신 200건 안에 실제 보드 대상은 11건뿐),
// parsedCases는 orderBy가 없어 문서 ID 순으로 잘려 "임의의 200건"이 표시됐다.
// 실측(2026-08-09): parsedCases processStage 보유 85건 전부 stageUpdatedAt 보유(결손 0) → 정렬 도입 안전.
//   workOrders 최근 1,000건이 약 82일치 = 현재 보드 대상 13건(가장 오래된 2026-06-08)을 모두 포함.
const PARSED_LIMIT = 500;
const ORDER_LIMIT = 1000;
const ORDER_WINDOW_DAYS = 90;   // 60일로 자르면 실측상 보드 대상 1건(06-08)이 사라져 90일로 둔다

// ── ①구역(제작 공정 보드·목록) 신선도 컷 ─────────────────────────────────
// 대표 확정 2026-08-09: 과거 기간을 지정한 [스캔하기]로 오래된 케이스가 한꺼번에 올라와
// 제작 공정 목록이 이미 끝난 옛 작업으로 채워지는 문제 → 파일 신호가 최근 14일 이내인 건만 ①에 둔다.
// 적용 범위는 ①구역만. ②'파일 없음'(의뢰서 경과일 기준)·③'의뢰서 없음'(미연결 파일 전체=청구 재료)에는 적용하지 않는다.
const STAGE_FRESH_DAYS = 14;

// ── 접수 칸(공정 기록 전 의뢰서) 신선도 컷 (2026-09-01 대표 승인 — 칸 추가 사양 P0) ──
// 접수 카드는 stageUpdatedAt이 없어 위 14일 컷이 안 걸린다 → createdAt 기준 30일 이내만 기본 표시,
// 초과분은 칸 안 "오래된 접수 N건 ▸" 접힘(멈춤 판정·개수 배지에서 제외). createdAt을 못 읽으면 표시 유지.
const RECEIVED_FRESH_DAYS = 30;
const isReceivedFresh = (o) => o._days === null || o._days <= RECEIVED_FRESH_DAYS;
// 치과 '구강스캔 감지' 칸: 감지(스캔) 시각 14일 이내만 기본 표시, 초과분은 "오래된 스캔 N건 ▸" 접힘
const SCAN_FRESH_DAYS = 14;
const SCAN_LIMIT = 200;   // ClinicRecentScans와 동일 한도 — 도달 시 칸에 잘림 소문구 표시

// ── 날짜 유틸 ──
const toDate = (v) => {
    if (!v) return null;
    if (typeof v.toDate === 'function') return v.toDate();
    if (v instanceof Date) return v;
    if (typeof v === 'string' || typeof v === 'number') {
        const d = new Date(v);
        return isNaN(d) ? null : d;
    }
    return null;
};
const getDueDate = (o) => toDate(o.deliveryDate || o.dueDate || o.completionDate || o.deliveryDueDate);
const endOfTomorrow = () => { const d = new Date(); d.setDate(d.getDate() + 1); d.setHours(23, 59, 59, 999); return d.getTime(); };
const startOfToday = () => { const d = new Date(); d.setHours(0, 0, 0, 0); return d.getTime(); };

const tsToMs = (t) => {
    if (!t) return 0;
    if (typeof t.toDate === 'function') return t.toDate().getTime();
    const d = new Date(t);
    return isNaN(d) ? 0 : d.getTime();
};

// 어떤 형태의 시각이든 ms로 — Firestore Timestamp / Date / 숫자(ms) / 문자열.
// parsedCases.fileMtime은 실측(2026-08-09) 전부 ISO 문자열이고 형식이 섞여 있다
// ("2026-07-20T15:46:32+09:00" 오프셋형, "2025-09-19T04:04:40.015Z" UTC형) — 숫자로 가정해 비교하면 안 된다.
// 판독 불가면 null을 돌려주고, 호출부는 null을 '제외'가 아니라 '포함'으로 다룬다(조용한 소실 방지).
const anyToMs = (v) => {
    if (v === null || v === undefined || v === '') return null;
    if (typeof v === 'number') return isFinite(v) ? v : null;
    if (typeof v.toDate === 'function') {
        try { const d = v.toDate(); return isNaN(d.getTime()) ? null : d.getTime(); } catch (e) { return null; }
    }
    if (v instanceof Date) return isNaN(v.getTime()) ? null : v.getTime();
    const d = new Date(v);
    if (isNaN(d.getTime())) {
        console.warn('제작 현황: 시각을 읽지 못했습니다(그대로 표시합니다):', v);
        return null;
    }
    return d.getTime();
};

// ①구역 판정에 쓰는 '파일 신호 시각'
//  · 수집 케이스(_isParsed): 파일 시각(fileMtime) 우선, 없으면 공정 기록 시각(stageUpdatedAt)
//  · 웹 의뢰서: 공정 기록이 갱신된 시각(stageUpdatedAt) = 파일 신호가 들어온 시각
// 어느 쪽도 못 읽으면 null → 호출부에서 표시 유지.
const stageSignalMs = (row) => {
    if (row && row._isParsed) {
        const fm = anyToMs(row.fileMtime);
        if (fm !== null) return fm;
    }
    return anyToMs(row && row.stageUpdatedAt);
};

// embedded=true: 대시보드 등 다른 화면 안에 보드만 끼워 넣는 모드 (페이지 배경·제목·상태줄 숨김, 로직은 동일 재사용)
function ProductionBoard({ user, embedded = false }) {
    const [mode, setMode] = useState(null);          // 'lab' | 'clinic' | null(판정 중)
    const [orders, setOrders] = useState([]);
    // 주문 없는 수집분(parsedCases) — 에이전트가 processStage를 기록한 케이스만 (기공소 뷰 전용)
    const [parsedList, setParsedList] = useState([]);
    // 단계 기록이 아직 없는 수집분 (파싱만 된 케이스 — 2026-08-11)
    const [parsedAllList, setParsedAllList] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    // 표시 한도 도달 여부 (조회 창을 꽉 채웠다 = 더 오래된 건이 화면에 안 나올 수 있다)
    const [ordersCapped, setOrdersCapped] = useState(false);
    const [parsedCapped, setParsedCapped] = useState(false);
    const [pendingStages, setPendingStages] = useState({});   // { orderId: stage } — 쓰기 완료 전 낙관 표시
    const [movingId, setMovingId] = useState(null);
    // 치과 모드: 기공소 에이전트 가동 여부 — true(가동)/false(미가동)/null(확인 불가)
    const [labAgentOk, setLabAgentOk] = useState({});
    const labAgentRef = useRef({});
    // 기공소 모드: 수집 폴더가 지정되지 않은 우리 PC 대수 (0 = 배너 미표시)
    const [noFolderPcs, setNoFolderPcs] = useState([]);
    // 검색·보기 전환 (2026-08-07 대표 요청) — embedded 대시보드는 캘린더 고정·툴바 미노출(2026-09-29)
    const [qText, setQText] = useState('');
    // 밀링 단계 영구 체크(가공·3D 프린트) 낙관 표시 — { pendKey: { millChecked?, printChecked? } }
    const [pendingChecks, setPendingChecks] = useState({});
    // 정렬 localStorage 유지 (P1-2, 2026-08-12 대표 승인 — productionView2와 같은 패턴)
    const [sortKey, setSortKey] = useState(() => {   // 'due'(납기 임박) | 'stage'(단계) | 'updated'(최근 갱신)
        try {
            const v = localStorage.getItem('prodSortKey');
            return ['due', 'stage', 'updated'].includes(v) ? v : 'due';
        } catch (e) { return 'due'; }
    });
    const setSortKeyPersist = (v) => {
        setSortKey(v);
        try { localStorage.setItem('prodSortKey', v); } catch (e) { /* 사파리 프라이빗 등 — 기억만 포기 */ }
    };
    const [showPastDue, setShowPastDue] = useState(false);   // 하단 [지난 납기 N건 보기] 펼침
    // 파일 없음 구역 (W1-1, 2026-08-09 대표 확정) — 기공소 모드 전용
    // 치과별 보기 localStorage 유지 (P1-2, 2026-08-12 대표 승인). 저장 키가 현재 목록에 없으면
    // 렌더 시 무시(clinicFilterEff) — 빈 화면 방지 (P2-14 지적과 동일 원리)
    const [clinicFilter, setClinicFilter] = useState(() => {      // 치과별 보기 ('' = 전체, 2026-08-11)
        try { return localStorage.getItem('prodClinicFilter') || ''; } catch (e) { return ''; }
    });
    const setClinicFilterPersist = (v) => {
        setClinicFilter(v);
        try { localStorage.setItem('prodClinicFilter', v); } catch (e) { /* 기억만 포기 */ }
    };
    const [showNoFile, setShowNoFile] = useState(false);          // 기본 접힘 (목록 보기 접수 구역)
    // 접수 칸 (2026-09-01 칸 추가) — 오래된 접수(30일 초과) 펼침 여부 (보드·목록 공용, 기본 접힘)
    const [showOldReceived, setShowOldReceived] = useState(false);
    // 치과 모드: 구강스캔 감지 칸 (scans 컬렉션 미연결분) + 오래된 스캔/오래된 접수 펼침
    const [clinicScans, setClinicScans] = useState([]);
    const [scansCapped, setScansCapped] = useState(false);   // limit(200) 도달 — 잘림 소문구용
    const [showOldScans, setShowOldScans] = useState(false);
    const [showOldClinicReceived, setShowOldClinicReceived] = useState(false);
    // 스캔 수집 플래그 (검수 P1) — CreateOrder와 동일 기준: clinicScanIntake && (베타 || stage 2 이상).
    // OFF면 구강스캔 칸 자체 미표시 + scans 구독도 돌지 않는다. '의뢰서 접수' 칸은 플래그와 무관.
    const [features, setFeatures] = useState(null);
    useEffect(() => {
        let alive = true;
        getFeatures().then(f => { if (alive) setFeatures(f); });
        return () => { alive = false; };
    }, []);
    const scanIntakeOn = isClinicScanIntakeOn(features, user, user?.email);
    const [showNoOrder, setShowNoOrder] = useState(false);        // ③ 의뢰서 없음(수집 케이스) 구역 — 기본 접힘
    const [hideUnknown, setHideUnknown] = useState(() => {        // 「단계 미확인」 숨기기 (P0-④, 2026-08-12)
        try { return localStorage.getItem('prodHideUnknown') === '1'; } catch (e) { return false; }
    });
    const [connIds, setConnIds] = useState(null);                 // 연결된 거래처 uid Set (null=조회 전/실패)
    const [connError, setConnError] = useState(null);             // 조회 실패 사유 (조용한 실패 금지)
    // 재택 반출 배지 (2026-08-17, 기획서_작업현황_반출표시 §2 A안) — 기공소 모드 전용
    const [homeCases, setHomeCases] = useState([]);               // joinHomeCases 결과
    const [hwNames, setHwNames] = useState({});                   // 웹에서 바꾼 PC 이름표 (agentStatus.deviceNames)
    // 덴클 꺼진 PC 경고 (2026-08-17 김레오니드 건 — LEDGER 나중에볼것 #29, 대표 지시로 구현):
    // 밀링 PC가 꺼져 있으면 공정 자동 신호가 조용히 멈춘다 — 화면에서 원인을 바로 보이게 한다.
    const [agentDevices, setAgentDevices] = useState([]);
    // 멈춤 기준표 접이식 (2026-08-25 P1 — 기준 공개 원칙)
    const [showStallGuide, setShowStallGuide] = useState(false);
    // 상단 확인 요약 바 펼침 (2026-10-08 대표 지적 "안내문이 너무 많다" — 배너 4개를 바 1개로 접음, 기기별 기억)
    const [alertsOpen, setAlertsOpen] = useState(() => {
        try { return localStorage.getItem('prodAlertsOpen') === '1'; } catch (e) { return false; }
    });
    const toggleAlertsOpen = () => {
        setAlertsOpen(v => {
            const nv = !v;
            try { localStorage.setItem('prodAlertsOpen', nv ? '1' : '0'); } catch (e) { /* 사파리 프라이빗 등 — 기억만 포기 */ }
            return nv;
        });
    };
    // 🦷 치과 모드: 기공소가 공개한 수집 케이스 진행 — 서버 중계(CF getClinicCaseProgress) 응답만 사용.
    // parsedCases를 직접 읽지 않는다 (2026-08-17 대표 승인, 기획서_치과공정공개_재설계 §2).
    const [clinicShared, setClinicShared] = useState({ labs: [], cases: [], loaded: false, error: null });
    const [clinicSharedBusy, setClinicSharedBusy] = useState(false);
    const navigate = useNavigate();
    // 디자인 3D 확인 (2026-09-29 캘린더 2차) — OrderList.openDesignPreview 복제. 읽기 전용, 완료 처리 시 서버가 파일을 삭제한다
    const [stlPreview, setStlPreview] = useState(null);
    const openDesignPreview = (order) => {
        const files = (order?.designPreview && Array.isArray(order.designPreview.files)) ? order.designPreview.files.filter(f => f?.url) : [];
        if (!files.length) { alert('열 수 있는 3D 디자인 파일이 없습니다.'); return; }
        const ordered = [...files].sort((a, b) => String(a.tooth || '').localeCompare(String(b.tooth || '')));
        setStlPreview({
            entries: ordered.map(f => ({ url: f.url, path: f.path, name: (f.path || '').split('/').pop(), tooth: f.tooth, kind: f.kind, format: f.format, size: f.size, reduced: !!f.reduced, layer: f.layer || null, part: f.part || null })),
            title: `3D 디자인 확인 · ${order.patientName || ''}`,
            notice: '디자인 확인용이며 최종 보철과 다를 수 있습니다. 완료 처리 후 자동 삭제됩니다.',
        });
    };
    // 보기 전환 상태(view/setViewPersist, localStorage productionView3) 제거 — 2026-10-25 대표 지시 캘린더 전용. effView 는 아래에서 'calendar' 고정.

    const targetId = user?.companyId || user?.uid;

    // ── 사업자 유형 판정 (OrderList와 동일: 직원은 회사 문서 폴백) ──
    useEffect(() => {
        let cancelled = false;
        const resolve = async () => {
            let bt = user?.businessType || user?.companyBusinessType;
            if ((user?.userType === 'staff' || user?.businessType === 'staff') && user?.companyId) {
                bt = user?.companyBusinessType;
                if (!bt) {
                    try {
                        const snap = await getDoc(doc(db, 'users', user.companyId));
                        if (snap.exists()) {
                            const d = snap.data();
                            bt = d.businessType || d.companyBusinessType;
                        }
                    } catch (e) {
                        console.error('제작 현황: 회사 유형 조회 실패:', e);
                    }
                }
            }
            if (cancelled) return;
            if (bt === 'lab' || bt === '기공소') setMode('lab');
            else if (bt === 'clinic' || bt === 'dental' || bt === '치과') setMode('clinic');
            else {
                setError('기공 현황은 기공소·치과 계정에서 사용할 수 있습니다.');
                setLoading(false);
            }
        };
        resolve();
        return () => { cancelled = true; };
    }, [user?.uid, user?.companyId, user?.businessType, user?.companyBusinessType, user?.userType]);

    // ── 주문 실시간 구독 ──
    useEffect(() => {
        if (!mode || !targetId) return undefined;
        setLoading(true);
        const field = mode === 'lab' ? 'toUserId' : 'fromUserId';
        // 등호(toUserId/fromUserId) + 같은 필드(createdAt) 범위·정렬이라 기존 복합 인덱스를 그대로 쓴다(신규 인덱스 불필요).
        const since = new Date(Date.now() - ORDER_WINDOW_DAYS * 24 * 60 * 60 * 1000);
        const q = query(
            collection(db, 'workOrders'),
            where(field, '==', targetId),
            where('createdAt', '>=', since),
            orderBy('createdAt', 'desc'),
            limit(ORDER_LIMIT)
        );
        const unsub = onSnapshot(q, (snapshot) => {
            setOrdersCapped(snapshot.size >= ORDER_LIMIT);
            const list = snapshot.docs
                .map(d => ({ id: d.id, ...d.data() }))
                // OrderList 관행: 메시지·환자명 없는 이메일 제외, 에이전트 수집분(agent_auto)은 명세서 전용
                .filter(o => o.type !== 'message')
                .filter(o => !(o.source === 'email' && !o.patientName))
                .filter(o => o.source !== 'agent_auto')
                .filter(o => (mode === 'clinic' ? o.hiddenForSender !== true : true));
            setOrders(list);
            setLoading(false);
            setError(null);
        }, (e) => {
            console.error('제작 현황 스트림 오류:', e);
            setError('주문 정보를 불러오지 못했습니다: ' + (e?.message || e));
            setLoading(false);
        });
        return () => unsub();
    }, [mode, targetId]);

    // ── 재택 반출 기록 1회 조회 (기공소 모드 전용 — 치과 모드에는 절대 미노출, 기획서 §2-1) ──
    // "환자 파일이 다른 PC로 나갔다"는 표시는 치과에 불안만 유발하므로 조회 자체를 하지 않는다.
    useEffect(() => {
        if (mode !== 'lab' || !targetId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const [snap, st] = await Promise.all([
                    getDocs(query(
                        collection(db, 'agentTransfers'),
                        where('fromOrgId', '==', targetId),
                        where('purpose', '==', 'home_work'),
                    )),
                    getDoc(doc(db, 'agentStatus', targetId)),
                ]);
                if (cancelled) return;
                setHomeCases(joinHomeCases(snap.docs.map(d => ({ id: d.id, ...d.data() }))));
                setHwNames((st.exists() && st.data().deviceNames) || {});
                setAgentDevices(Object.values((st.exists() && st.data().devices) || {}).filter(d => d && d.deviceId));
            } catch (e) {
                // 배지만 빠질 뿐 보드는 정상 — 환자명 없이 사유만 기록 (조용한 실패 금지)
                console.error('제작 현황: 반출 기록 조회 실패:', e?.message || e);
            }
        })();
        return () => { cancelled = true; };
    }, [mode, targetId]);

    // ── 🦷 치과 모드: 공개된 수집 케이스 조회 (진입 시 1회 + [새로 고침]) ──
    const loadClinicShared = useCallback(async () => {
        if (clinicSharedBusy) return;
        setClinicSharedBusy(true);
        try {
            const fn = httpsCallable(getFunctions(undefined, 'asia-northeast3'), 'getClinicCaseProgress');
            const res = await fn({});
            const d = (res && res.data) || {};
            setClinicShared({ labs: d.labs || [], cases: d.cases || [], loaded: true, error: null });
        } catch (e) {
            // 배지·카드만 빠질 뿐 웹 의뢰서 보드는 정상 (조용한 실패 금지 — 사유는 화면·콘솔에)
            console.error('제작 현황: 공개 케이스 조회 실패:', e?.message || e);
            setClinicShared(prev => ({ ...prev, loaded: true, error: e?.message || String(e) }));
        } finally {
            setClinicSharedBusy(false);
        }
    }, [clinicSharedBusy]);
    useEffect(() => {
        if (mode !== 'clinic' || !targetId) return;
        loadClinicShared();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, targetId]);

    // ── 🦷 치과 모드: 구강스캔 감지 칸 (2026-09-01 칸 추가) ──
    // scans 컬렉션(clinicId 본인 — rules 성립 확인됨)에서 의뢰서가 아직 연결 안 된 스캔을 실시간 구독.
    // ClinicRecentScans와 같은 쿼리(등호+detectedAt 정렬, 기존 인덱스 재사용). 의뢰서가 붙으면(orderId 생김)
    // 스냅샷 반영으로 칸에서 자동으로 빠진다 — 전이는 데이터 필터만으로 성립.
    useEffect(() => {
        // 플래그 OFF면 구독 자체를 돌리지 않는다 (검수 P1 — CreateOrder와 동일: 숨김 시 조회도 안 함)
        if (mode !== 'clinic' || !targetId || !scanIntakeOn) {
            setClinicScans([]);
            setScansCapped(false);
            return undefined;
        }
        const q = query(
            collection(db, 'scans'),
            where('clinicId', '==', targetId),
            orderBy('detectedAt', 'desc'),
            limit(SCAN_LIMIT)
        );
        const unsub = onSnapshot(q, (snapshot) => {
            setScansCapped(snapshot.size >= SCAN_LIMIT);   // 잘림을 숨기지 않는다 (검수 P2-2)
            setClinicScans(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
        }, (e) => {
            // 조용한 실패 금지 — 스캔 칸만 빠질 뿐 나머지 보드는 정상
            console.error('제작 현황: 구강스캔 목록 조회 실패:', e?.message || e);
        });
        return () => unsub();
    }, [mode, targetId, scanIntakeOn]);

    // ── 주문 없는 수집분 구독 (기공소 뷰만 — 치과 계정과 연결 안 된 데이터라 치과 뷰 미노출) ──
    // labId==·processStage in 5단계 + stageUpdatedAt 최신순. done은 서버에서 제외.
    // 정렬을 넣었으므로 복합 인덱스(labId, processStage, stageUpdatedAt desc)가 필요하다 — firestore.indexes.json에 추가·배포.
    // 인덱스가 아직 없거나 stageUpdatedAt 없는 문서 때문에 실패하면 정렬 없는 쿼리로 1회 폴백(조용히 0건이 되지 않게).
    // 에이전트가 parsedCases에 processStage를 아직 안 쓰는 동안은 0건(빈 결과)이 정상.
    useEffect(() => {
        if (mode !== 'lab' || !targetId) return undefined;
        let unsub = null;
        let cancelled = false;
        const subscribe = (sorted) => {
            const parts = [
                collection(db, 'parsedCases'),
                where('labId', '==', targetId),
                where('processStage', 'in', STAGE_KEYS),
            ];
            const q = sorted
                ? query(...parts, orderBy('stageUpdatedAt', 'desc'), limit(PARSED_LIMIT))
                : query(...parts, limit(PARSED_LIMIT));
            unsub = onSnapshot(q, (snapshot) => {
                if (cancelled) return;
                setParsedCapped(snapshot.size >= PARSED_LIMIT);
                setParsedList(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            }, (e) => {
                // 조용한 실패 금지 — 수집분만 못 불러와도 주문 보드는 유지
                console.error('제작 현황: 수집분 스트림 오류:', e);
                if (sorted && !cancelled) {
                    console.warn('제작 현황: 수집분 정렬 조회 실패 — 정렬 없이 다시 시도합니다(인덱스 확인 필요).');
                    if (unsub) unsub();
                    subscribe(false);
                }
            });
        };
        subscribe(true);
        return () => { cancelled = true; if (unsub) unsub(); };
    }, [mode, targetId]);

    // ── 단계 기록이 없는 수집분 구독 (대표 지시 2026-08-11: "파싱하는 파일은 다 나오게") ──
    // 종전에는 에이전트가 processStage를 쓴 건만 보드에 올라와, 파싱은 됐지만 공정 신호가 안 붙은
    // 케이스(실측 아이반 최근 14일 114건 중 50건)가 화면에서 통째로 빠져 있었다.
    // 파일 시각(fileMtime) 최신순으로 따로 가져와 합친다. 신선도 컷(14일)은 기존과 동일하게 걸린다.
    // fileMtime은 ISO 문자열이라 사전순=시간순(형식이 섞여도 날짜 부분은 같은 자리) — 정렬 근거로 충분하다.
    useEffect(() => {
        if (mode !== 'lab' || !targetId) return undefined;
        let unsub = null;
        let cancelled = false;
        const subscribe = (sorted) => {
            const parts = [collection(db, 'parsedCases'), where('labId', '==', targetId)];
            const q = sorted
                ? query(...parts, orderBy('fileMtime', 'desc'), limit(PARSED_LIMIT))
                : query(...parts, limit(PARSED_LIMIT));
            unsub = onSnapshot(q, (snapshot) => {
                if (cancelled) return;
                if (snapshot.size >= PARSED_LIMIT) setParsedCapped(true);   // 한도 도달을 숨기지 않는다
                setParsedAllList(snapshot.docs.map(d => ({ id: d.id, ...d.data() })));
            }, (e) => {
                // 조용한 실패 금지 — 이 스트림이 죽어도 단계 기록분 보드는 그대로 유지된다
                console.error('제작 현황: 수집분(단계 미기록) 스트림 오류:', e);
                if (sorted && !cancelled) {
                    console.warn('제작 현황: fileMtime 정렬 실패 — 정렬 없이 재시도(인덱스 확인 필요).');
                    if (unsub) unsub();
                    subscribe(false);
                }
            });
        };
        subscribe(true);
        return () => { cancelled = true; if (unsub) unsub(); };
    }, [mode, targetId]);

    // ── 연결된 거래처 목록 (기공소 모드 · '파일 없음' 구역 대상 판정에만 사용) ──
    // 관행대로 requesterId/receiverId 양방향 accepted만 조회(전체 스캔 금지). 1회성 getDocs — 보드 스트림과 무관.
    useEffect(() => {
        if (mode !== 'lab' || !targetId) return undefined;
        let cancelled = false;
        (async () => {
            try {
                const [s1, s2] = await Promise.all([
                    getDocs(query(collection(db, 'connections'), where('requesterId', '==', targetId), where('status', '==', 'accepted'))),
                    getDocs(query(collection(db, 'connections'), where('receiverId', '==', targetId), where('status', '==', 'accepted'))),
                ]);
                if (cancelled) return;
                const ids = new Set();
                [...s1.docs, ...s2.docs].forEach(d => {
                    const c = d.data();
                    const pid = c.requesterId === targetId ? c.receiverId : c.requesterId;
                    if (pid) ids.add(pid);
                });
                setConnIds(ids);
                setConnError(null);
            } catch (e) {
                console.error('제작 현황: 거래처 목록 조회 실패:', e);
                if (!cancelled) setConnError(e?.message || '거래처 목록을 불러오지 못했습니다');
            }
        })();
        return () => { cancelled = true; };
    }, [mode, targetId]);

    // ── 파생: 진행 중 주문 (done·완료·취소·거절 제외) ──
    // 4단계(2026-09-04): received(열람·인쇄 등 확인 액션)는 '확인' 칸, stage 미기록은 '의뢰서 접수' 칸(receivedOrders — 아래 별도 파생).
    // 확인 액션(열람·인쇄·다운로드·CAD) 시 received 기록(markOrderReceivedStage) = 접수→확인 자동 이동.
    // pendingStages 값 = 표시 열 키 (낙관 표시)
    // 2026-10-12: 기공소·치과 모드 모두 3단계(labColOf — stage3Of)
    const colWithPending = (pendKey, row) => {
        const pend = pendingStages[pendKey];
        if (pend) return pend === DONE ? null : pend;
        return labColOf(row);
    };
    const active = useMemo(() => {
        return orders
            .map(o => ({
                ...o,
                _col: colWithPending(o.id, o),
                _due: getDueDate(o),
            }))
            .filter(o => o._col !== null)   // 미기록(접수 칸)·done 제외
            .filter(o => !['completed', 'cancelled', 'canceled', 'rejected'].includes(o.status || ''));   // rejected: 구 접수 칸 기준 승계
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, orders, pendingStages]);

    // 상대 업체명
    const partnerName = (o) => mode === 'lab'
        ? (o.fromCompanyName || o.dentistName || o.fromUserName || o.internalLabName || o.senderName || '업체명 없음')
        : (o.toCompanyName || o.labName || o.toUserName || o.receiverName || '기공소');

    // ── 파생: 주문 없는 수집분 카드 (기공소 뷰 · 최근 30일 · 중복 제거) ──
    // 중복 제거 규칙 (우선순위 주문 > 수집 > 스캔):
    //  ① 치과명 있는 수집 카드: 정규화(소문자·공백/특수문자 제거) 환자명+치과명 키가 주문 카드와 같으면 숨김
    //  ② scan_only·치과명 빈값 카드: 치과명이 없으므로 환자명 단독 키로 주문 카드·①의 수집 카드와 대조해 겹치면 숨김
    const normKey = (s) => (s || '').toString().toLowerCase().replace(/[^0-9a-z가-힣]/g, '');
    const parsedActive = useMemo(() => {
        if (mode !== 'lab') return [];
        const cutoff = Date.now() - 30 * 24 * 60 * 60 * 1000;
        const orderKeys = new Set(active.map(o => normKey(o.patientName) + '|' + normKey(partnerName(o))));
        const orderNameKeys = new Set(active.map(o => normKey(o.patientName)));
        // 단계 기록분 + 미기록분 병합 (같은 문서는 단계 기록분 우선)
        const merged = new Map();
        parsedAllList.forEach(r => merged.set(r.id, r));
        parsedList.forEach(r => merged.set(r.id, r));
        // 이미 끝낸 건은 보드에 올리지 않는다 (검토 지적 P1-1, 2026-08-11).
        //   단계 없이 가져오는 스트림이 done 문서까지 끌어와, 완료 처리한 카드가
        //   다음 렌더에 「단계 미확인」으로 첫 칸에 되살아났다. 눌러도 사라지지 않는다.
        //   (2026-09-04 4단계: received는 '확인' 칸으로 매핑되므로 더 이상 제외하지 않는다 — 되돌리기로 received가 된 카드가 사라지면 안 된다)
        //   (2026-09-29: 구 shipping(옛 저장값)도 완료와 같이 제외 — 남기면 rawColOf null이라 「단계 미확인」으로 되살아난다)
        for (const [id, r] of [...merged]) {
            if (r.processStage === DONE || r.processStage === 'shipping') merged.delete(id);
        }
        const mapped = [...merged.values()]
            .map(r => {
                const clinic = (r.partnerName || r.clientNameInFile || '').trim();
                // 공정 신호가 아직 없는 케이스: CAD 케이스 파일이 있다는 사실만 확실하므로
                // 첫 공정 칸(확인)에 두고 「단계 미확인」으로 표시한다 — 단계를 지어내지 않는다.
                // (2026-10-12) 기공소 3단계 판정 — 공정 신호(processStage·체크·designDone 등)가 하나라도 있으면 known
                const known = !!labColOf(r);
                return {
                    ...r,
                    _isParsed: true,
                    _isScanOnly: r.sourceType === 'scan_only',
                    _stageUnknown: !known,
                    _col: known ? colWithPending('pc_' + r.id, r)
                        : (pendingStages['pc_' + r.id] === DONE ? null : (pendingStages['pc_' + r.id] || 'confirm')),
                    _name: displayPatientName(r).name || r.patientName || '환자미상',
                    _clinic: clinic,
                    _noClinic: r.sourceType === 'scan_only' || !clinic,
                };
            })
            .filter(r => r._col !== null)
            .filter(r => {
                const t = tsToMs(r.stageUpdatedAt) || (r.fileMtime ? new Date(r.fileMtime).getTime() : 0);
                return t >= cutoff;
            });
        // ① 치과명 있는 수집 카드 — 주문과 환자+치과 키 대조
        const withClinic = mapped
            .filter(r => !r._noClinic)
            .filter(r => !orderKeys.has(normKey(r._name) + '|' + normKey(r._clinic)));
        const withClinicNameKeys = new Set(withClinic.map(r => normKey(r._name)));
        // ② 스캔만·치과명 없는 카드 — 환자명 단독 키로 주문·수집 카드와 대조
        const scanOnly = mapped
            .filter(r => r._noClinic)
            .filter(r => !orderNameKeys.has(normKey(r._name)) && !withClinicNameKeys.has(normKey(r._name)));
        const merged2 = [...withClinic, ...scanOnly];
        // 「단계 미확인」 숨기기 스위치 — 신호가 안 붙은 옛 수집분이 화면을 덮을 때 한 번에 정리 (삭제 아님)
        return hideUnknown ? merged2.filter(r => !r._stageUnknown) : merged2;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, parsedList, parsedAllList, active, pendingStages, hideUnknown]);

    // ── 재택 반출 배지 매칭 (A안 — caseLabels '환자명_치과명' 원문을 카드와 정규화 대조) ──
    // 진행 중 반출 + 최근 7일 내 회신 건만 배지 대상 (7일 지난 회신은 소음 방지 자동 소멸, 기획서 §2-1)
    const homeBadges = useMemo(() => {
        if (mode !== 'lab' || homeCases.length === 0) return { both: new Map(), name: new Map(), labels: [], open: [] };
        const both = new Map(); const name = new Map(); const labels = []; const open = [];
        const retCutoff = Date.now() - 7 * 86400000;
        for (const c of homeCases) {
            const activeOut = c.status !== 'returned';
            const recentReturn = c.status === 'returned' && c.retAt && c.retAt.getTime() >= retCutoff;
            if (!activeOut && !recentReturn) continue;
            const info = { ...c, pcName: deviceLabelOf(hwNames, c.targetDeviceId, c.targetDeviceName || 'PC') };
            if (activeOut) open.push(info);
            for (const raw of c.rawLabels) {
                const { patient, clinic } = splitCaseLabel(raw);
                const pk = normKey(patient);
                if (pk) {
                    const bk = pk + '|' + normKey(clinic);
                    // 최신순 정렬이므로 먼저 온 것(최신 반출)을 대표로 유지
                    if (!both.has(bk)) both.set(bk, info);
                    if (!name.has(pk)) name.set(pk, info);
                }
                // 포함 관계 폴백용 — 실제 폴더명은 `날짜_주문번호-치과약칭환자명…` 형식이 많아
                // (아이반 실측 2026-08-17: 20/20이 이 형식) 분해 대신 라벨 전체에서 환자명 포함을 본다
                const ln = normKey(raw);
                if (ln) labels.push({ ln, info });
            }
        }
        return { both, name, labels, open };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, homeCases, hwNames]);

    // 매칭 안 된 진행 중 반출 건수 — 배지 없이 사라지면 조용한 소실이므로 상단 안내줄로 받는다 (기획서 §2-1)
    const homeUnmatched = useMemo(() => {
        if (mode !== 'lab' || homeBadges.open.length === 0) return 0;
        const keys = new Set(); const nameKeys = new Set();
        active.forEach(o => {
            const pk = normKey(o.patientName);
            if (pk) { keys.add(pk + '|' + normKey(partnerName(o))); nameKeys.add(pk); }
        });
        parsedActive.forEach(r => {
            const pk = normKey(r._name);
            if (pk) { keys.add(pk + '|' + normKey(r._clinic)); nameKeys.add(pk); }
        });
        const nameList = [...nameKeys].filter(n => n.length >= 2);   // 포함 폴백은 2글자 이상만 (오탐 방지)
        return homeBadges.open.filter(c => !c.rawLabels.some(raw => {
            const { patient, clinic } = splitCaseLabel(raw);
            const pk = normKey(patient);
            if (pk && (keys.has(pk + '|' + normKey(clinic)) || nameKeys.has(pk))) return true;
            const ln = normKey(raw);
            return !!ln && nameList.some(n => ln.includes(n));
        })).length;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, homeBadges, active, parsedActive]);

    // ── 🦷 치과 모드: 공개 수집 케이스 → 카드 행 (웹 의뢰서와 병합, 주문 우선 dedup) ──
    // CF 응답(화이트리스트)만으로 만든 읽기 전용 행 — 웹 의뢰서와 같은 환자+같은 기공소면 주문 카드가 이긴다.
    const clinicCollected = useMemo(() => {
        if (mode !== 'clinic' || clinicShared.cases.length === 0) return [];
        const orderKeys = new Set(orders.map(o => normKey(o.patientName) + '|' + (o.toUserId || '')));
        return clinicShared.cases
            .filter(c => !orderKeys.has(normKey(c.patientName) + '|' + (c.labId || '')))
            .map(c => {
                const row = {
                    id: 'cc_' + c.id,
                    patientName: c.patientName || '(환자명 없음)',
                    toUserId: c.labId,
                    toCompanyName: c.labName || '기공소',
                    processStage: c.stage,
                    designDone: c.designDone === true,
                    stageUpdatedAt: c.stageUpdatedAtMs ? new Date(c.stageUpdatedAtMs) : null,
                    fileMtime: c.fileMtime || null,
                    dueDate: c.dueDate || null,
                    _fromShare: true,
                };
                return { ...row, _col: colWithPending(row.id, row), _due: getDueDate(row) };
            })
            .filter(r => r._col !== null);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, clinicShared, orders, pendingStages]);

    // ── 파생: '의뢰서 접수' 칸 의뢰서 (2026-09-01 — 구 ②'파일 없음' 구역을 접수 칸으로 흡수, 모집단 동일 / 2026-09-04 processStage 없음만) ──
    // 실측(2026-08-09): 아이반랩 30일 웹 의뢰서 148건 중 105건이 공정 기록이 없어 보드에 아예 뜨지 않았다.
    // 대상: 연결된 거래처가 보낸 웹 의뢰서 중 ①공정 기록 없음(processStage 없음) ②수집 케이스와도 안 붙음
    //       ③완료·취소·거절 아님. (agent_auto는 orders 스트림에서 이미 제외)
    // 데이터 출처는 위 orders 스트림(최근 ORDER_WINDOW_DAYS일) 그대로 — 추가 쿼리를 만들지 않는다.
    // ★ 접수 카드는 active(공정 보드 모집단)에 넣지 않는다 — parsedActive의 orderKeys 중복제거가 바뀌면 안 된다.
    const receivedOrders = useMemo(() => {
        if (mode !== 'lab' || !connIds) return [];
        // 수집 케이스와 붙었는지 대조. 단계 기록이 없는 수집분(parsedAllList)까지 봐야
        // 같은 환자가 ②'파일 없음'과 ③'의뢰서 없음'에 동시에 뜨지 않는다 (검토 지적 P1-2, 2026-08-11).
        // 검수 P2-1 (2026-09-01): 환자명 단독 키(pcNameKeys) 폐기 — 같은 이름이면 "다른 치과"
        // 수집 케이스와도 접수에서 빠져 보드 사각지대가 생겼다. 환자명+정규화 치과명 조합 키만 쓴다
        // (parsedCases에는 fromUserId가 없어 치과명 문자열 대조). 치과명 없는 수집 케이스(스캔만 등)와는
        // 이제 dedup하지 않는다 — 양쪽에 보일 수는 있어도 안 보이는 것(사각지대)보다 낫다.
        const pcKeys = new Set();
        [...parsedList, ...parsedAllList].forEach(r => {
            const nm = normKey(displayPatientName(r).name || r.patientName || '');
            const cl = normKey(r.partnerName || r.clientNameInFile || '');
            if (nm && cl) pcKeys.add(nm + '|' + cl);
        });
        const now = Date.now();
        return orders
            .filter(o => o.fromUserId && connIds.has(o.fromUserId))                     // 연결된 치과만
            // 공정 기록 없음 = 기공소가 아직 안 본 의뢰서 (received는 2026-09-04부터 '확인' 칸 — active 쪽)
            // 2026-10-12 3단계: processStage가 없어도 stage3Of 신호(인쇄·다운로드 기록·QR 체크 등)가 있으면 공정 칸(active)으로 간다 — 이중 표시 방지
            .filter(o => !o.processStage && !pendingStages[o.id] && stage3Of(o).key === null)
            .filter(o => !['completed', 'cancelled', 'canceled', 'rejected'].includes(o.status || ''))
            .filter(o => !o.boardHiddenAt)                                              // [×] 숨김 (2026-09-03) — 아래 receivedHidden
            .filter(o => {
                const nm = normKey(o.patientName);
                if (!nm) return true;
                return !pcKeys.has(nm + '|' + normKey(partnerName(o)));   // 같은 치과일 때만 dedup (검수 P2-1)
            })
            .map(o => {
                const created = toDate(o.createdAt);
                return {
                    ...o,
                    _due: getDueDate(o),
                    _created: created,
                    _days: created ? Math.max(0, Math.floor((now - created.getTime()) / 86400000)) : null,
                };
            })
            .sort((a, b) => (b._days === null ? -1 : b._days) - (a._days === null ? -1 : a._days));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, orders, parsedList, parsedAllList, connIds, pendingStages]);
    // [×]로 숨긴 접수 (2026-09-03) — 접수 칸 아래 "숨긴 접수 N건" 접힘에서 되살릴 수 있다. 의뢰서 자체는 그대로.
    const receivedHidden = useMemo(() => (mode !== 'lab' || !connIds) ? [] : orders
        .filter(o => o.boardHiddenAt && o.fromUserId && connIds.has(o.fromUserId))
        .filter(o => !o.processStage && stage3Of(o).key === null)
        .filter(o => !['completed', 'cancelled', 'canceled', 'rejected'].includes(o.status || '')), [mode, orders, connIds]);
    const [showHiddenReceived, setShowHiddenReceived] = useState(false);
    const setReceivedHidden = async (order, hide) => {
        try {
            await updateDoc(doc(db, 'workOrders', order.id), {
                boardHiddenAt: hide ? serverTimestamp() : null,
                boardHiddenBy: hide ? (user?.uid || null) : null,
                updatedAt: serverTimestamp(),
            });
        } catch (e) {
            console.error('제작 현황: 접수 숨김/되살리기 실패:', order.id, e);
            alert((hide ? '숨기지' : '되살리지') + ' 못했습니다.\n' + (e?.message || '잠시 후 다시 시도해 주세요.'));
        }
    };

    // ── 접수 칸 동명 확인 (2026-09-01 사양 A-5) — 같은 치과+정규화 환자명 2건 이상이면 안내 배지 ──
    // 자동 케이스 연결은 에이전트가 이미 동명 시 보류한다 — 웹은 "확인하라"는 안내만 한다.
    const receivedDupCounts = useMemo(() => {
        const m = new Map();
        if (mode !== 'lab') return m;
        receivedOrders.forEach(o => {
            const k = (o.fromUserId || '') + '|' + normKey(o.patientName);
            m.set(k, (m.get(k) || 0) + 1);
        });
        return m;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, receivedOrders]);

    // ── 🦷 치과 모드: 구강스캔 감지 칸 행 (미연결 + expired 제외, 14일 신선도 분리) ──
    const clinicScanRows = useMemo(() => {
        if (mode !== 'clinic') return { fresh: [], old: [] };
        const cutoff = Date.now() - SCAN_FRESH_DAYS * 86400000;
        const fresh = []; const old = [];
        clinicScans
            .filter(s => !s.orderId && s.status !== 'expired' && !s.dismissedAt)   // 「오늘」 ×로 숨긴 스캔 제외 (09-03 점검 #2)
            .forEach(s => {
                const ms = anyToMs(s.scannedAt || s.detectedAt);
                // 시각을 못 읽은 스캔은 감추지 않는다 (조용한 소실 방지)
                ((ms === null || ms >= cutoff) ? fresh : old).push(s);
            });
        return { fresh, old };
    }, [mode, clinicScans]);

    // ── 🦷 치과 모드: 의뢰서 접수 칸 행 (기공소 공정 신호 전 = processStage 없음 — 30일 신선도 분리, 사양 B-2) ──
    // active 필터(labColOf 접수→null)의 예외를 여기서 표시용으로만 되살린다. received는 '의뢰서 확인' 칸(active).
    // 2026-10-12 3단계: processStage가 없어도 stage3Of 신호(인쇄·다운로드 기록·체크 등)가 있으면 공정 칸으로 간다 — 기공소 모드와 같은 이중 표시 방지
    const clinicReceivedRows = useMemo(() => {
        if (mode !== 'clinic') return { fresh: [], old: [] };
        const now = Date.now();
        const fresh = []; const old = [];
        orders
            .filter(o => !o.processStage && !pendingStages[o.id] && stage3Of(o).key === null)
            .filter(o => !['completed', 'cancelled', 'canceled', 'rejected'].includes(o.status || ''))
            .map(o => {
                const created = toDate(o.createdAt);
                return {
                    ...o,
                    _due: getDueDate(o),
                    _days: created ? Math.max(0, Math.floor((now - created.getTime()) / 86400000)) : null,
                };
            })
            .forEach(o => (isReceivedFresh(o) ? fresh : old).push(o));
        return { fresh, old };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, orders, pendingStages]);

    // ── 확인 전화 카드 — 제거됨 (2026-09-03 대표 지시 "삭제해줘"; 석고 확인은 08-27에 먼저 제거). 되살리려면 git 이력 참조.


    // ── 치과 모드: 기공소 목록·배지 색 (이름순 고정 → 색 순환 안정) ──
    const labList = useMemo(() => {
        if (mode !== 'clinic') return [];
        const map = new Map();
        [...active, ...clinicCollected].forEach(o => {
            const id = o.toUserId || '';
            if (!map.has(id)) map.set(id, { labId: id, name: partnerName(o) });
        });
        return [...map.values()].sort((a, b) => a.name.localeCompare(b.name, 'ko'));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [active, clinicCollected, mode]);
    const labColor = (labId) => {
        const idx = labList.findIndex(l => l.labId === labId);
        return C.cat[(idx < 0 ? 0 : idx) % C.cat.length];
    };
    const labInitial = (name) => (name || '?').trim().charAt(0) || '?';

    // ── 치과 모드: 기공소 에이전트 하트비트 (15분 초과=미가동, 읽기 실패=확인 불가) ──
    useEffect(() => {
        if (mode !== 'clinic' || labList.length === 0) return undefined;
        let cancelled = false;
        const check = async () => {
            const next = { ...labAgentRef.current };
            for (const { labId } of labList) {
                if (!labId) continue;
                try {
                    const snap = await getDoc(doc(db, 'agentStatus', labId));
                    if (!snap.exists()) { next[labId] = false; continue; }
                    const data = snap.data();
                    const devices = Object.values(data.devices || {});
                    if (!devices.length && data.lastSeen) devices.push(data);   // 구버전 호환
                    const latest = devices.reduce((m, d) => Math.max(m, tsToMs(d.lastSeen)), 0);
                    next[labId] = !!(latest && Date.now() - latest < AGENT_ALIVE_MS);
                } catch (e) {
                    // 읽기 실패 = 판별 불가(null) — '연결 전'으로 단정하지 않는다
                    if (next[labId] === undefined) console.warn('제작 현황: 기공소 하트비트 조회 실패:', labId, e?.code || e?.message);
                    next[labId] = null;
                }
            }
            if (!cancelled) { labAgentRef.current = next; setLabAgentOk(next); }
        };
        check();
        const t = setInterval(check, 5 * 60 * 1000);
        return () => { cancelled = true; clearInterval(t); };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [mode, labList.map(l => l.labId).join(',')]);

    // ── 기공소 모드: 수집 폴더 미지정 PC 점검 (2026-08-09 대표 지시) ──
    // 수집 폴더가 하나도 없는 PC는 그 PC에서 CAD 작업을 해도 케이스가 올라오지 않는데 화면엔 정상 연결로 보인다.
    // 판정은 stmtFolderPaths 배열이 있고 길이 0인 경우만 (구버전 에이전트는 미보고 → 제외, utils/agentFolders.js).
    // 오래 안 쓰는 PC까지 경고하지 않도록 최근 30일 내 신호가 있는 PC만 센다.
    useEffect(() => {
        if (mode !== 'lab' || !targetId) return undefined;
        let cancelled = false;
        getDoc(doc(db, 'agentStatus', targetId))
            .then(snap => {
                if (cancelled || !snap.exists()) return;
                const devices = Object.values(snap.data().devices || {});
                const recent = devices.filter(d => {
                    const ms = tsToMs(d.lastSeen);
                    return ms && (Date.now() - ms) <= 30 * 86400000;
                });
                setNoFolderPcs(recent.filter(isMissingStmtFolder).map(d => d.deviceName || d.deviceId || 'PC'));
            })
            .catch(e => console.warn('제작 현황: 수집 폴더 점검 실패(배너 생략):', e?.code || e?.message));
        return () => { cancelled = true; };
    }, [mode, targetId]);

    // ── 수동 이동 (표시 열 기준) — rules hasOnly 공정 필드(designDone 포함)만 update, 실패 시 알림+롤백 ──
    // 카드 1건의 단계 쓰기 payload — moveStage(개별)·moveIntakeToConfirm이 같은 것을 쓴다
    const stageWritePayload = (order, w) => {
        // parsedCases(수집 카드)는 주문 문서가 아니라 status 개념이 없다 — 공정 필드만 쓴다
        const { status, ...stageOnly } = w;
        const payload = order._isParsed ? stageOnly : w;
        return {
            ...payload,
            stageUpdatedAt: serverTimestamp(),
            stageSource: 'manual',
            // 이력에 환자명 미포함 (rules·에이전트와 동일)
            stageHistory: arrayUnion({ stage: w.processStage, at: new Date().toISOString(), source: 'manual' }),
        };
    };
    const moveStage = async (order, toCol) => {
        const w = writeForCol[toCol];
        if (!w) return;
        if (movingId) return;
        // 안전망 — 버튼은 숨기지만 다른 경로로 호출돼도 밀링 기록을 지우지 않는다 (2026-10-12 검토 P2-1)
        if (revertBlocked(order, toCol)) { alert(REVERT_BLOCKED_MSG); return; }
        // 수집분 카드는 parsedCases 문서에 동일 필드 update (rules: hasCompanyAccess(labId) 전체 update 허용 — firestore.rules parsedCases 절)
        const coll = order._isParsed ? 'parsedCases' : 'workOrders';
        const pendKey = order._isParsed ? 'pc_' + order.id : order.id;
        setMovingId(order.id);
        setPendingStages(prev => ({ ...prev, [pendKey]: toCol }));   // 낙관 표시 (표시 열 키)
        try {
            await updateDoc(doc(db, coll, order.id), stageWritePayload(order, w));
        } catch (e) {
            console.error('제작 현황: 단계 이동 실패:', order.id, e);
            alert('단계 이동에 실패했습니다.\n' + (e?.message || '잠시 후 다시 시도해 주세요.'));
        } finally {
            // 성공이면 스냅샷이 서버 값을 곧 반영, 실패면 원래 단계로 화면 복귀
            setPendingStages(prev => { const n = { ...prev }; delete n[pendKey]; return n; });
            setMovingId(null);
        }
    };

    // ── 의뢰서 접수 → 확인 수동 이동 (2026-09-04 4단계 — 구 접수→스캔완료 버튼(09-01 A-6) 대체) ──
    // markOrderReceivedStage(의뢰서 열람·인쇄 시 자동 기록)와 같은 값 processStage:'received'를 쓴다 — status는 건드리지 않는다(같은 규약).
    // 필드는 processStage/stageUpdatedAt/stageSource/stageHistory = rules hasOnly 통과(markOrderReceivedStage와 동일 집합).
    const moveIntakeToConfirm = async (order) => {
        if (movingId) return;
        setMovingId(order.id);
        // 낙관 표시: 접수 칸(receivedOrders)은 pendingStages[o.id]가 생기면 즉시 빠지고,
        // active는 colWithPending으로 확인 열에 올린다. 실패 시 pending 삭제로 원위치.
        setPendingStages(prev => ({ ...prev, [order.id]: 'confirm' }));
        try {
            await updateDoc(doc(db, 'workOrders', order.id),
                stageWritePayload(order, { processStage: 'received' }));
        } catch (e) {
            console.error('제작 현황: 접수→확인 이동 실패:', order.id, e);
            alert('의뢰서 확인 칸으로 옮기지 못했습니다.\n' + (e?.message || '잠시 후 다시 시도해 주세요.'));
        } finally {
            setPendingStages(prev => { const n = { ...prev }; delete n[order.id]; return n; });
            setMovingId(null);
        }
    };

    // (구) 4번째 열 일괄 완료(completeAllShipping, P1-1 2026-08-12) — 2026-09-29 열 제거로 삭제. 완료는 서버 자동(auto_due).

    // ── 밀링 단계 영구 체크 (가공 millChecked · 3D 프린트 printChecked — 2026-08-07 대표 확정) ──
    // 문서에 bool 저장(재로그인 무관 유지). workOrders rules 공정 분기 = hasOnly 7필드 + processStage in [...] 검증이라
    // 체크 단독 update에도 processStage를 함께 실어 보낸다(designDone 단독 기록 때와 동일 처리).
    // 값은 문서의 processStage 그대로(4단계에서는 한 칸에 designing/milling이 섞여 열에서 역산하면 밀링을 디자인으로 되돌리게 된다 — 2026-09-04).
    // 낙관 이동 중(pendingStages)에만 이동 목표 열의 값을 쓴다. 같은 값이면 affectedKeys에 안 잡혀 hasOnly 통과.
    // parsedCases는 필드 제한 없음(hasCompanyAccess만).
    const pendKeyOf = (o) => (o._isParsed ? 'pc_' + o.id : o.id);
    const checkVal = (o, field) => {
        const pend = pendingChecks[pendKeyOf(o)];
        if (pend && pend[field] !== undefined) return pend[field];
        return o[field] === true;
    };
    // 밀링 신호 여부 — 판정 기준은 clinic-stage.js stage3Of ④의 밀링 조건과 같다(가공 체크는 낙관값 포함)
    const hasMillSignal = (o) => !!o && (o.processStage === 'milling' || checkVal(o, 'millChecked') ||
        checkMapDone(o, 'mill') || checkMapDone(o, 'milling') || checkMapDone(o, 'implantMill'));
    // 되돌리기(→의뢰서 확인) 가능 여부 — 막히면 버튼 대신 REVERT_BLOCKED_MSG
    const revertBlocked = (o, prevCol) => prevCol === 'confirm' && hasMillSignal(o);
    const toggleCheck = async (order, field) => {
        const coll = order._isParsed ? 'parsedCases' : 'workOrders';
        const pendKey = pendKeyOf(order);
        const nextVal = !checkVal(order, field);
        const pendCol = pendingStages[pendKey];
        const stage = (pendCol && writeForCol[pendCol] ? writeForCol[pendCol].processStage : null) || order.processStage;
        if (!stage) {
            // 조용한 실패 금지 (2026-10-12 — 3단계 판정으로 processStage 없는 카드도 디자인 완료 칸에 올 수 있어 화면에도 알린다)
            console.error('제작 현황: 체크 저장 중단 — 단계 값을 알 수 없음:', order.id);
            alert('이 케이스는 공정 기록이 없어 가공·3D 체크를 저장하지 못했습니다. 의뢰서를 한 번 열어 본 뒤 다시 시도해 주세요.');
            return;
        }
        setPendingChecks(prev => ({ ...prev, [pendKey]: { ...prev[pendKey], [field]: nextVal } }));   // 낙관 표시
        try {
            await updateDoc(doc(db, coll, order.id), { [field]: nextVal, processStage: stage });
        } catch (e) {
            console.error('제작 현황: 밀링 체크 저장 실패:', order.id, field, e);
            alert('체크 저장에 실패했습니다.\n' + (e?.message || '잠시 후 다시 시도해 주세요.'));
        } finally {
            // 성공=로컬 스냅샷이 즉시 새 값 반영(지연 보상), 실패=원래 값으로 복귀
            setPendingChecks(prev => {
                const n = { ...prev, [pendKey]: { ...prev[pendKey] } };
                delete n[pendKey][field];
                if (Object.keys(n[pendKey]).length === 0) delete n[pendKey];
                return n;
            });
        }
    };

    // ── 납기 판정 (상단 칩 타일은 전부 제거 — 2026-08-05 대표 지시, 납기 경고는 보드 카드 빨간 표시로 유지) ──
    const eot = endOfTomorrow();
    const sot = startOfToday();
    const isOverdue = (o) => o._due && o._due.getTime() < sot;
    const isDueSoon = (o) => o._due && o._due.getTime() <= eot;

    const dueText = (o) => {
        if (!o._due) return null;
        const t = `납기 ${o._due.getMonth() + 1}/${o._due.getDate()}`;
        return isOverdue(o) ? `${t} 지남` : t;
    };
    // 내원일 표시 (2026-08-25 P2 — 내원일 입력 부활 D2). 멈춤 임계 단축(tightVisit)은
    // stallBadgeEl(getStallInfo)이 이미 반영 — 여기는 카드에 날짜만 보여 준다.
    const visitText = (o) => {
        const v = toDate(o.visitDate);
        return v ? `내원 ${v.getMonth() + 1}/${v.getDate()}` : null;
    };

    // ── 스타일 (디자인 정본 인라인 이식) ──
    const S = {
        page: embedded
            ? { background: 'transparent', padding: '12px 14px', boxSizing: 'border-box' }
            : { flex: 1, minHeight: '100vh', background: C.plane, padding: '24px 28px', boxSizing: 'border-box' },
        h1: { fontSize: 20, margin: 0, color: C.textPrimary, fontWeight: 700 },
        statusline: { fontSize: 12.5, color: C.muted, margin: '4px 0 18px', display: 'flex', alignItems: 'center', gap: 6 },
        dotLive: { width: 7, height: 7, borderRadius: '50%', background: C.good, display: 'inline-block' },
        urgentDot: { width: 6, height: 6, borderRadius: '50%', background: C.critical, flexShrink: 0 },
        boardHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: 10, flexWrap: 'wrap', gap: 8 },
        boardTitle: { fontSize: 14, fontWeight: 700, color: C.textPrimary },
        legend: { display: 'flex', gap: 10, alignItems: 'center', flexWrap: 'wrap' },
        legendItem: { display: 'flex', alignItems: 'center', gap: 5, fontSize: 11.5, color: C.textSecondary },
        badge: (bg) => ({ display: 'inline-flex', alignItems: 'center', justifyContent: 'center', width: 18, height: 18, borderRadius: 5, fontSize: 9.5, fontWeight: 700, color: '#fff', background: bg, flexShrink: 0 }),
        board: { display: 'flex', gap: 10, alignItems: 'flex-start' },
        col: { flex: 1, minWidth: 0 },
        colHead: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', fontSize: 12, fontWeight: 700, color: C.textSecondary, padding: '0 4px 8px', borderBottom: `2px solid ${C.gridline}`, marginBottom: 8 },
        colCount: { color: C.muted, fontWeight: 600 },
        cards: { display: 'flex', flexDirection: 'column', gap: 7, minHeight: 60, maxHeight: '60vh', overflowY: 'auto' },
        card: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 10px', fontSize: 12.5 },
        cardDim: { background: '#f8fafc', border: `1px dashed ${C.border}`, borderRadius: 8, padding: '9px 10px', fontSize: 12.5, color: C.muted },
        patient: { fontWeight: 700, color: C.textPrimary, display: 'flex', alignItems: 'center', gap: 6 },
        sub: { color: C.muted, fontSize: 11, marginTop: 3 },
        due: { color: C.textSecondary, fontSize: 11, marginTop: 3 },
        dueLate: { color: C.critical, fontWeight: 700, fontSize: 11, marginTop: 3 },
        btnRow: { display: 'flex', gap: 5, marginTop: 7 },
        btn: { flexShrink: 0, border: `1px solid ${C.border}`, background: C.plane, borderRadius: 6, fontSize: 11, padding: '4px 8px', cursor: 'pointer', color: C.textSecondary, fontFamily: 'inherit', fontWeight: 600 },
        btnPrimary: { flexShrink: 0, border: '1px solid rgba(99,102,241,0.35)', background: '#eef2ff', borderRadius: 6, fontSize: 11, padding: '4px 8px', cursor: 'pointer', color: C.cat[0], fontFamily: 'inherit', fontWeight: 700 },
        empty: { fontSize: 12.5, color: '#9ca3af', textAlign: 'center', padding: '20px 8px', border: `1px solid ${C.gridline}`, borderRadius: 12, background: C.surface },
        // "완료" 배지 제거됨(2026-08-05 4열 완료형 — 디자인완료 열 자체가 그 의미)
        // 주문서 없는 수집분 표시 (에이전트 수집 케이스)
        // 공정 신호가 아직 안 붙은 수집분 — 회색으로 눈에 덜 띄게 (지어낸 단계가 아님을 알린다)
        unknownBadge: { display: 'inline-flex', alignItems: 'center', flexShrink: 0, fontSize: 9.5, fontWeight: 700, color: C.muted, background: 'rgba(100,116,139,0.10)', border: '1px solid rgba(100,116,139,0.28)', borderRadius: 5, padding: '1px 5px' },
        parsedBadge: { display: 'inline-flex', alignItems: 'center', flexShrink: 0, fontSize: 9.5, fontWeight: 700, color: C.cat[0], background: 'rgba(99,102,241,0.08)', border: '1px solid rgba(99,102,241,0.30)', borderRadius: 5, padding: '1px 5px' },
        // 스캔 자료만 있는 폴더(의뢰서 파일 없음, sourceType scan_only) — 회색 계열
        scanBadge: { display: 'inline-flex', alignItems: 'center', flexShrink: 0, fontSize: 9.5, fontWeight: 700, color: C.textSecondary, background: '#f1f5f9', border: `1px solid ${C.border}`, borderRadius: 5, padding: '1px 5px' },
        // 재택 반출 배지 (2026-08-17) — 파랑=나가 있음, 초록=회신됨, 노랑 테두리=하루 경과 경고
        hwBadge: { display: 'inline-block', fontSize: 10.5, fontWeight: 700, color: '#4f46e5', background: '#eef2ff', border: '1px solid #e0e7ff', borderRadius: 6, padding: '2px 7px', whiteSpace: 'nowrap', cursor: 'pointer', maxWidth: '100%', overflow: 'hidden', textOverflow: 'ellipsis', verticalAlign: 'middle' },
        hwBadgeBack: { color: '#0b6e5d', background: '#e6f5f1', border: '1px solid #cdeae2' },
        hwBadgeWarn: { color: '#8a6100', background: '#fff6e0', border: '1px solid #f0dcae' },
        // 검색·보기 전환 툴바 (2026-08-07 — 검색 입력은 명세서/주문목록 검색창과 같은 톤)
        toolbar: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 10, marginBottom: 12, flexWrap: 'wrap' },
        searchWrap: { position: 'relative', flex: '0 1 260px', minWidth: 180 },
        searchInput: { width: '100%', boxSizing: 'border-box', padding: '6px 26px 6px 10px', border: '1px solid #e2e8f0', borderRadius: 8, fontSize: 13, color: '#334155', outline: 'none', fontFamily: 'inherit', background: C.surface },
        searchClear: { position: 'absolute', right: 6, top: '50%', transform: 'translateY(-50%)', background: 'none', border: 'none', cursor: 'pointer', padding: 2, fontSize: 14, color: '#94a3b8', lineHeight: 1 },
        sortSel: { border: `1px solid ${C.border}`, background: C.surface, borderRadius: 8, fontSize: 12, padding: '5px 8px', color: C.textSecondary, fontFamily: 'inherit', cursor: 'pointer' },
        toggleBtn: { border: `1px solid ${C.border}`, background: C.surface, borderRadius: 8, fontSize: 12, padding: '5px 10px', color: C.textSecondary, fontFamily: 'inherit', cursor: 'pointer', fontWeight: 700 },
        // 보기 전환 3분할 [보드][목록][캘린더] (2026-09-29 캘린더 추가 — 구 ⇄ 토글 버튼 대체, 기본값 보드 유지)
        segWrap: { display: 'inline-flex', border: `1px solid ${C.border}`, borderRadius: 8, overflow: 'hidden', background: C.surface },
        segBtn: (on) => ({ border: 'none', borderRight: `1px solid ${C.border}`, background: on ? '#eef2ff' : C.surface, fontSize: 12, padding: '5px 10px', color: on ? C.cat[0] : C.textSecondary, fontFamily: 'inherit', cursor: 'pointer', fontWeight: 700 }),
        // 목록 보기
        listWrap: { display: 'flex', flexDirection: 'column', gap: 6 },
        listRow: { display: 'flex', alignItems: 'center', gap: 12, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', fontSize: 12.5 },
        // 지난 납기 접이식 줄
        pastDueBar: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: '#fdf3f3', border: '1px solid rgba(208,59,59,0.30)', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, fontWeight: 700, color: C.critical, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' },
        // 파일 없음 접이식 줄 (W1-1) — 경고가 아니라 점검용이라 호박색 계열
        noFileBar: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: '#fdfaf0', border: '1px solid rgba(237,161,0,0.35)', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, fontWeight: 700, color: '#8a6100', cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' },
        noFileRow: { display: 'flex', alignItems: 'center', gap: 12, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '8px 12px', fontSize: 12.5, cursor: 'pointer' },
        // 의뢰서 없음(수집 케이스) 접이식 줄 (③) — 수집 배지와 같은 파랑 계열
        noOrderBar: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, background: '#eef2ff', border: '1px solid rgba(99,102,241,0.30)', borderRadius: 8, padding: '8px 12px', fontSize: 12.5, fontWeight: 700, color: C.cat[0], cursor: 'pointer', fontFamily: 'inherit', textAlign: 'left' },
        // 밀링 체크(가공·3D) — 체크되면 진하게
        checkLabel: (on) => ({ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 11, cursor: 'pointer', color: on ? C.textPrimary : C.muted, fontWeight: on ? 700 : 600, whiteSpace: 'nowrap' }),
        checkBox: { margin: 0, width: 13, height: 13, cursor: 'pointer', accentColor: C.cat[0] },
        checkMini: { display: 'inline-flex', alignItems: 'center', flexShrink: 0, fontSize: 9.5, fontWeight: 700, color: C.textSecondary, background: '#f1f5f9', border: `1px solid ${C.border}`, borderRadius: 5, padding: '1px 5px' },
        // 멈춤 배지 (2026-08-25 P1) — 기준 초과 = 적색
        stallBadge: { display: 'inline-flex', alignItems: 'center', flexShrink: 0, fontSize: 9.5, fontWeight: 700, color: C.critical, background: '#fdf3f3', border: '1px solid rgba(208,59,59,0.35)', borderRadius: 5, padding: '1px 5px', whiteSpace: 'nowrap' },
        // 멈춤 요약 스트립 + 기준표 접이식
        stallGuideBox: { background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '10px 14px', marginTop: 8, fontSize: 12, color: C.textSecondary },
        // 확인할 것 요약 바 (2026-10-08) — 바탕은 중립, 가장 심각한 칩 1개만 적색
        alertWrap: { marginBottom: 12, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8 },
        alertBar: { width: '100%', display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, padding: '8px 12px', background: 'transparent', border: 'none', cursor: 'pointer', fontFamily: 'inherit', fontSize: 12.5, textAlign: 'left' },
        alertChip: { fontSize: 12, fontWeight: 600, color: C.textSecondary, background: '#f1f5f9', borderRadius: 999, padding: '1px 9px', whiteSpace: 'nowrap' },
        alertChipTop: { fontSize: 12, fontWeight: 700, color: C.critical, background: '#fdf3f3', border: '1px solid rgba(208,59,59,0.30)', borderRadius: 999, padding: '0 8px', whiteSpace: 'nowrap' },
        alertToggle: { flexShrink: 0, fontSize: 12, fontWeight: 700, color: C.muted, whiteSpace: 'nowrap' },
        alertList: { borderTop: `1px solid ${C.border}`, padding: '4px 12px 8px' },
        alertRow: { padding: '7px 0', fontSize: 12.5, lineHeight: 1.6, borderBottom: '1px dashed #eef2f6' },
        alertLink: { flexShrink: 0, border: `1px solid ${C.border}`, background: '#fff', borderRadius: 6, fontSize: 11.5, padding: '3px 9px', cursor: 'pointer', color: C.textPrimary, fontFamily: 'inherit', fontWeight: 700, whiteSpace: 'nowrap' },
        // 접수 칸 (2026-09-01 칸 추가) — 스캔 도착=초록, 동명 확인=호박, 칸 설명 캡션, 오래된 건 접힘 줄
        scanArriveBadge: { display: 'inline-flex', alignItems: 'center', flexShrink: 0, fontSize: 9.5, fontWeight: 700, color: C.good, background: '#eefaf0', border: '1px solid rgba(12,163,12,0.30)', borderRadius: 5, padding: '1px 5px', whiteSpace: 'nowrap' },
        dupBadge: { display: 'inline-flex', alignItems: 'center', flexShrink: 0, fontSize: 9.5, fontWeight: 700, color: '#8a6100', background: '#fff6e0', border: '1px solid #f0dcae', borderRadius: 5, padding: '1px 5px', whiteSpace: 'nowrap' },
        colCaption: { fontSize: 10.5, color: C.muted, padding: '0 4px 6px' },
        hideX: { flexShrink: 0, width: 18, height: 18, lineHeight: '16px', padding: 0, borderRadius: '50%', border: `1px solid ${C.border}`, background: '#fff', color: C.muted, fontSize: 13, fontWeight: 700, cursor: 'pointer', fontFamily: 'inherit', textAlign: 'center', marginLeft: 4 },
        oldFoldBar: { width: '100%', border: `1px dashed ${C.border}`, background: '#f8fafc', borderRadius: 8, fontSize: 11.5, padding: '7px 10px', cursor: 'pointer', color: C.muted, fontFamily: 'inherit', fontWeight: 700, textAlign: 'left' },
    };

    // 다음 이동 버튼 라벨 (기공소 표시 열 기준, 2026-10-12 3단계). 디자인 완료 칸은 다음 버튼 없음 — 포장 완료는 공정 체크의 포장 체크로만.
    const nextLabelOf = { confirm: '▸ ' + STAGE3_LABELS.designDone };
    // 열 구성 — 기공소·치과 모드 공통 3단계(LAB_COLUMNS, 2026-10-12)
    const viewColumns = LAB_COLUMNS;
    const viewStageColumns = viewColumns.filter(c => c[0] !== 'intake');

    // 디자인완료·밀링완료 칸 체크 UI(가공·3D — 구 밀링 열 체크 승계) — 기공소=체크박스, 치과=읽기 전용 소형 배지(체크된 것만)
    const MILL_CHECKS = [['millChecked', '가공'], ['printChecked', '3D']];
    const millCheckboxes = (o) => CHECK_COLS.includes(o._col) && (
        <div style={{ display: 'flex', gap: 10, marginTop: 6 }}>
            {MILL_CHECKS.map(([f, lb]) => {
                const on = checkVal(o, f);
                return (
                    <label key={f} style={S.checkLabel(on)}>
                        <input type="checkbox" checked={on} style={S.checkBox}
                            onChange={() => toggleCheck(o, f)} />
                        {lb}
                    </label>
                );
            })}
        </div>
    );
    // ── 멈춤 배지 (2026-08-25 P1) — 파생 계산만, 기공소 모드 전용. 낙관 이동 중(pendingStages)엔
    // 문서의 옛 단계 기준 판정이라 순간 어긋날 수 있어 이동 중 카드는 배지를 생략한다.
    const stallBadgeEl = (o) => {
        if (mode !== 'lab') return null;
        if (pendingStages[o._isParsed ? 'pc_' + o.id : o.id]) return null;
        if (o._col === 'packed') return null;   // 포장 완료 칸(2026-10-12)은 멈춤 대상 아님 — 남은 공정이 없다
        const st = getStallInfo(o);
        if (!st.stalled) return null;
        return (
            <span style={S.stallBadge}
                title={`${st.label} 단계 ${st.daysInStage}일째 (기준 ${st.threshold}일${st.tightVisit ? ' — 내원 임박으로 1일 적용' : ''})`}>
                {`⚠ ${st.daysInStage}일째`}
            </span>
        );
    };
    // (구) 4번째 열 카드 도착 확인 배지(receiptBadgeEl) — 2026-09-29 제거(치과 확인 버튼 자체가 없어짐, 법무 검토 항목 4)

    // 확인 칸 세부 신호(저장값 그대로 — 툴팁·소배지로만 표시, 2026-09-04). 디자인완료·밀링완료 칸은 열 이름이 곧 신호라 소배지 없음(2026-09-29 4단계)
    const workDetailEl = (o) => {
        let txt = null;
        // 확인 칸 세부 신호 (대표 요청 2026-09-04): 디자인 케이스 감지 / 스캔 감지. 열람·인쇄만(received)은 표시 없음
        if (o._col === 'confirm') txt = o.processStage === 'designing' ? '디자인 감지' : (o.processStage === 'scanning' ? '스캔 감지' : null);
        return txt ? <span style={S.checkMini} title={`덴클 프로그램이 감지한 세부 신호: ${txt}`}>{txt}</span> : null;
    };
    // 재제작/수리 소배지 (2026-09-04 대표 승인) — 웹 의뢰서만(수집 카드는 isRemake 의미가 다름), 구데이터=재제작
    const remakeBadgeEl = (o) => {
        if (!o || o._isParsed || !o.isRemake) return null;
        const c = remakeBadgeColors(o);
        return <span style={{ ...S.checkMini, background: c.bg, color: c.fg, border: `1px solid ${c.fg}55` }} title="치과가 지난 의뢰서를 불러와 만든 의뢰서입니다">{remakeLabelOf(o, '재제작')}</span>;
    };
    const millCheckBadges = (o) => CHECK_COLS.includes(o._col) && (
        <>
            {MILL_CHECKS.filter(([f]) => o[f] === true).map(([f, lb]) => (
                <span key={f} style={S.checkMini}>{lb + ' ✓'}</span>
            ))}
        </>
    );

    // ── 재택 반출 배지 (기공소 모드 전용) — 카드 행에서 환자·치과명으로 반출 기록을 찾는다 ──
    const fmtHw = (d) => (d ? `${d.getDate()}일 ${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}` : '');
    const homeInfoOf = (row) => {
        if (mode !== 'lab') return null;   // 치과 모드 미노출 가드 — 조회도 안 하지만 이중 방어
        const patient = row._isParsed ? row._name : row.patientName;
        const clinic = row._isParsed ? row._clinic : partnerName(row);
        const pk = normKey(patient);
        if (!pk) return null;
        const direct = homeBadges.both.get(pk + '|' + normKey(clinic)) || homeBadges.name.get(pk);
        if (direct) return direct;
        // 포함 관계 폴백 — 라벨(폴더명)에 이 카드의 환자명이 들어 있으면 그 반출로 본다 (2글자 이상만)
        if (pk.length >= 2) {
            const hit = homeBadges.labels.find(x => x.ln.includes(pk));
            if (hit) return hit.info;
        }
        return null;
    };
    const homeBadgeEl = (row, compact = false) => {
        const info = homeInfoOf(row);
        if (!info) return null;
        let text; let full; let warn = false; let back = false;
        if (info.status === 'returned') {
            back = true;
            full = `↩ 회신됨 r${info.rev} (${fmtHw(info.retAt)})`;
            text = compact ? `↩ r${info.rev}` : full;
        } else if (info.status === 'returning') {
            // 폴더 여러 개 묶음은 몇 개 돌아왔는지 함께 (homeReturnLabel — 재택근무 화면과 같은 판정, 2026-10-16)
            back = true;
            full = `↩ ${homeReturnLabel(info)}`;
            text = compact ? (info.retTotal > 1 ? `↩ ${info.retDone}/${info.retTotal}` : '↩') : full;
        } else if (info.status === 'returnLost') {
            warn = true;
            full = `↩ ${homeReturnLabel(info)} — ${HOME_RETURN_LOST_HINT}`;
            text = compact ? '↩ 일부 못 받음' : `↩ ${homeReturnLabel(info)}`;
        } else if (info.status === 'designing') {
            warn = info.idle;
            full = `🏠 ${info.pcName}에서 작업 중 (${fmtHw(info.gotAt)} 가져감)${info.idle ? ' · 하루 경과' : ''}`;
            text = compact ? `🏠 ${info.pcName}` : full;
        } else {
            warn = info.stuck;
            full = `🏠 ${info.pcName}로 보냄 — 아직 도착 안 함`;
            text = compact ? `🏠 ${info.pcName}` : full;
        }
        return (
            <span
                style={{ ...S.hwBadge, ...(back ? S.hwBadgeBack : {}), ...(warn ? S.hwBadgeWarn : {}) }}
                title={full + ' — 자세한 내용·조치는 재택근무 화면에서'}
                onClick={(e) => { e.stopPropagation(); navigate('/homework'); }}
            >{text}</span>
        );
    };

    const renderLabCard = (o) => {
        const nextCol = nextColOf[o._col];
        const prevCol = prevColOf[o._col] || null;
        return (
            <div key={(o._isParsed ? 'pc_' : '') + o.id} style={S.card}>
                <div style={S.patient}>
                    {!o._isParsed && isDueSoon(o) && <span style={S.urgentDot} />}
                    {o._isParsed ? o._name : (o.patientName || '(환자명 없음)')}
                    {o._isParsed && (
                        o._isScanOnly
                            ? <span style={S.scanBadge}>{'스캔'}</span>
                            : <span style={o._stageUnknown ? S.unknownBadge : S.parsedBadge}>{o._stageUnknown ? '단계 미확인' : '수집'}</span>
                    )}
                    {remakeBadgeEl(o)}
                    {workDetailEl(o)}
                    {!o._stageUnknown && stallBadgeEl(o)}
                </div>
                {/* 치과명 줄: 수집 카드는 파싱값(없으면 미확인), 스캔 카드는 치과명 없으면 줄 생략 */}
                {(!o._isParsed || o._clinic || !o._isScanOnly) && (
                    <div style={S.sub}>{o._isParsed ? (o._clinic || '치과 미확인') : partnerName(o)}</div>
                )}
                {homeBadgeEl(o) && <div style={{ marginTop: 5 }}>{homeBadgeEl(o)}</div>}
                {!o._isParsed && dueText(o) && <div style={isDueSoon(o) ? S.dueLate : S.due}>{dueText(o)}</div>}
                {!o._isParsed && visitText(o) && <div style={S.due}>{visitText(o)}</div>}
                {millCheckboxes(o)}
                <div style={S.btnRow}>
                    {prevCol && !revertBlocked(o, prevCol) && (
                        <button style={S.btn} disabled={movingId === o.id}
                            onClick={() => moveStage(o, prevCol)}>{'◂ 되돌리기'}</button>
                    )}
                    {prevCol && revertBlocked(o, prevCol) && (
                        <span style={{ ...S.sub, marginTop: 0, alignSelf: 'center' }}>{REVERT_BLOCKED_MSG}</span>
                    )}
                    {/* 다음 단계가 없는 칸(밀링완료)은 버튼 미표시 — 완료는 납기일 자정 서버 자동 처리 (2026-09-29) */}
                    {nextCol && (
                        <button style={S.btnPrimary} disabled={movingId === o.id}
                            onClick={() => moveStage(o, nextCol)}>{nextLabelOf[o._col]}</button>
                    )}
                </div>
            </div>
        );
    };

    const renderClinicCard = (o) => {
        const labId = o.toUserId || '';
        const ok = labAgentOk[labId];   // true/false/null(확인 불가)/undefined(조회 중)
        const name = o.patientName || '(환자명 없음)';
        if (ok === false) {
            // 미가동 기공소: 회색 카드로 표시 (데이터는 남기되 진행 상태가 갱신되지 않음을 알림)
            return (
                <div key={o.id} style={S.cardDim}>
                    <div style={{ ...S.patient, color: C.muted }}>
                        <span style={S.badge('#b6b4ad')}>{labInitial(partnerName(o))}</span>
                        {name}
                    </div>
                    <div style={S.sub}>{'연결 전 (에이전트 미가동)'}</div>
                </div>
            );
        }
        return (
            <div key={o.id} style={S.card}>
                <div style={S.patient}>
                    {isOverdue(o) && <span style={S.urgentDot} />}
                    <span style={S.badge(labColor(labId))}>{labInitial(partnerName(o))}</span>
                    {name}
                    {o._fromShare && <span style={S.parsedBadge}>{'수집'}</span>}
                    {remakeBadgeEl(o)}
                    {millCheckBadges(o)}
                </div>
            </div>
        );
    };

    // ── 접수 칸 카드 (기공소, 2026-09-01 칸 추가) — 아직 제작 시작 전 의뢰서 ──
    // isOld=true(오래된 접수 접힘분)는 멈춤 배지를 생략한다 (사양 A-3 — 판정 로직은 그대로, 표시만 필터).
    const receivedBadges = (o, isOld) => {
        const dupN = receivedDupCounts.get((o.fromUserId || '') + '|' + normKey(o.patientName)) || 0;
        return (
            <>
                {remakeBadgeEl(o)}
                {o.scanId && <span style={S.scanArriveBadge} title="치과 구강스캔이 이 의뢰서에 연결되어 있습니다">{'📷 스캔 도착'}</span>}
                {dupN >= 2 && <span style={S.dupBadge} title="같은 치과에 같은 이름의 접수가 여러 건 있습니다 — 케이스 연결 시 어느 건인지 확인해 주세요">{`동명 ${dupN}건 — 케이스 연결 시 확인`}</span>}
                {!isOld && stallBadgeEl(o)}
            </>
        );
    };
    const hideBtn = (o) => (
        <button onClick={(e) => { e.stopPropagation(); setReceivedHidden(o, true); }} style={S.hideX}
            title="접수 칸에서 숨기기 (의뢰서는 그대로 남고, 아래 '숨긴 접수'에서 되살릴 수 있습니다)">×</button>
    );
    const renderReceivedCard = (o, isOld = false) => (
        <div key={'rc_' + o.id} style={S.card}>
            <div style={S.patient}>
                {isDueSoon(o) && <span style={S.urgentDot} />}
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {o.patientName || '(환자명 없음)'}
                </span>
                {receivedBadges(o, isOld)}
                <span style={{ flex: 1 }} />
                {hideBtn(o)}
            </div>
            <div style={S.sub}>{partnerName(o)}</div>
            {dueText(o) && <div style={isDueSoon(o) ? S.dueLate : S.due}>{dueText(o)}</div>}
            {visitText(o) && <div style={S.due}>{visitText(o)}</div>}
            <div style={S.btnRow}>
                <button style={S.btn} onClick={() => navigate(`/view-order/${o.id}`)} title="의뢰서 열기">{'의뢰서'}</button>
                <button style={S.btnPrimary} disabled={movingId === o.id}
                    onClick={() => moveIntakeToConfirm(o)}>{'▸ 의뢰서 확인으로'}</button>
            </div>
        </div>
    );
    // 접수 목록 행 (목록 보기 — 구 ②'파일 없음' 행 형태 계승)
    const renderReceivedRow = (o, isOld = false) => {
        const hot = o._days !== null && o._days >= 7;
        return (
            <div key={'rc_' + o.id} style={S.noFileRow} onClick={() => navigate(`/view-order/${o.id}`)} title="의뢰서 열기">
                <div style={{ ...S.patient, flex: '1.2 1 0', minWidth: 0 }}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {o.patientName || '(환자명 없음)'}
                    </span>
                    {receivedBadges(o, isOld)}
                </div>
                <div style={{ color: C.muted, fontSize: 11.5, flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {partnerName(o)}
                </div>
                <div style={{ flex: '0 0 76px', fontSize: 11 }}>{listDue(o)}</div>
                <div style={{ flex: '0 0 88px', fontSize: 11.5, fontWeight: hot ? 700 : 600, color: hot ? C.critical : C.textSecondary }}>
                    {o._days === null ? '경과 미상' : `의뢰 ${o._days}일 경과`}
                </div>
                <button style={S.btnPrimary} disabled={movingId === o.id}
                    onClick={(e) => { e.stopPropagation(); moveIntakeToConfirm(o); }}>{'▸ 의뢰서 확인으로'}</button>
                {hideBtn(o)}
            </div>
        );
    };
    // 숨긴 접수 접힘 줄 + 되살리기 (보드·목록 공용)
    const renderHiddenReceivedFold = () => receivedHidden.length > 0 && (
        <>
            <button style={S.oldFoldBar} onClick={() => setShowHiddenReceived(v => !v)} title="[×]로 숨긴 접수 — 눌러서 펼치기">
                {(showHiddenReceived ? '▾ ' : '▸ ') + '숨긴 접수 ' + receivedHidden.length + '건'}
            </button>
            {showHiddenReceived && receivedHidden.map(o => (
                <div key={'rh_' + o.id} style={{ ...S.noFileRow, opacity: 0.7 }}>
                    <div style={{ ...S.patient, flex: '1 1 0', minWidth: 0 }}>
                        <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', textDecoration: 'line-through' }}>{o.patientName || '(환자명 없음)'}</span>
                    </div>
                    <div style={{ color: C.muted, fontSize: 11.5, flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{partnerName(o)}</div>
                    <button style={S.btn} onClick={() => navigate(`/view-order/${o.id}`)}>{'의뢰서'}</button>
                    <button style={S.btn} onClick={() => setReceivedHidden(o, false)}>{'되살리기'}</button>
                </div>
            ))}
        </>
    );

    // ── 🦷 치과 모드 카드: 구강스캔 감지 / 의뢰서 접수 (2026-09-01 사양 B) ──
    const renderScanCard = (s) => {
        const ms = anyToMs(s.scannedAt || s.detectedAt);
        const d = ms ? new Date(ms) : null;
        return (
            <div key={'sc_' + s.id} style={{ ...S.card, borderStyle: 'dashed' }} title="스캔만 있고 의뢰서가 없습니다 — 의뢰서 작성으로 연결">
                <div style={S.patient}>
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {s.patientNameGuess || s.caseLabel || '(이름 미확인)'}
                    </span>
                </div>
                <div style={S.sub}>{'스캔만 · 의뢰서 없음' + (d ? ` · ${d.getMonth() + 1}/${d.getDate()}` : '')}</div>
                <div style={S.btnRow}>
                    <button style={S.btnPrimary} onClick={() => navigate(`/create-order?scanId=${s.id}`)}>{'의뢰서 작성'}</button>
                </div>
            </div>
        );
    };
    const renderClinicReceivedCard = (o) => (
        <div key={'cr_' + o.id} style={S.card}>
            <div style={S.patient}>
                {isOverdue(o) && <span style={S.urgentDot} />}
                <span style={S.badge(labColor(o.toUserId || ''))}>{labInitial(partnerName(o))}</span>
                <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {o.patientName || '(환자명 없음)'}
                </span>
                {remakeBadgeEl(o)}
            </div>
            <div style={S.sub}>{partnerName(o)}</div>
            {dueText(o) && <div style={isOverdue(o) ? S.dueLate : S.due}>{dueText(o)}</div>}
        </div>
    );

    // ── 렌더 ──
    if (error) {
        return (
            <div style={S.page}>
                {!embedded && <h1 style={S.h1}>기공 현황</h1>}
                <div style={{ marginTop: embedded ? 0 : 20, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 10, padding: '18px 20px', fontSize: 13, color: C.critical }}>{error}</div>
            </div>
        );
    }
    if (loading || !mode) {
        return (
            <div style={S.page}>
                {!embedded && <h1 style={S.h1}>기공 현황</h1>}
                <div style={{ marginTop: embedded ? 0 : 20, fontSize: 13, color: C.muted }}>불러오는 중…</div>
            </div>
        );
    }

    // ── 검색 필터 (환자명 + 상대 업체명 부분 일치 — 공백·대소문자 무시) ──
    const qNorm = qText.trim().toLowerCase().replace(/\s+/g, '');
    const hitQ = (s) => (s || '').toString().toLowerCase().replace(/\s+/g, '').includes(qNorm);

    // ── ①구역 신선도 컷 (STAGE_FRESH_DAYS · 대표 확정 2026-08-09) ──
    // 보드·목록·지난 납기 접이식이 모두 이 목록에서 갈라져 나가므로 여기 한 곳에서만 자른다.
    // 시각을 못 읽은 건(파일 신호 없음)은 제외하지 않고 그대로 둔다 — 공정 기록이 있는데 화면에서 사라지면 안 된다.
    // 접수(receivedOrders — 자체 30일 컷 별도)·③'의뢰서 없음'(parsedActive)은 이 컷을 타지 않는다.
    const freshCutoff = Date.now() - STAGE_FRESH_DAYS * 24 * 60 * 60 * 1000;
    // 치과 모드는 기공소가 공개한 수집 케이스(clinicCollected, 읽기 전용)를 웹 의뢰서와 합쳐 본다
    const allActive = mode === 'clinic' ? [...active, ...clinicCollected] : active;
    const freshRows = allActive.filter(o => {
        const ms = stageSignalMs(o);
        return ms === null ? true : ms >= freshCutoff;
    });
    const staleHidden = allActive.length - freshRows.length;
    const qOrders = qNorm ? freshRows.filter(o => hitQ(o.patientName) || hitQ(partnerName(o))) : freshRows;
    const qParsed = qNorm ? parsedActive.filter(r => hitQ(r._name) || hitQ(r._clinic)) : parsedActive;
    const qReceived = qNorm ? receivedOrders.filter(o => hitQ(o.patientName) || hitQ(partnerName(o))) : receivedOrders;

    // ── 치과별 보기 (대표 지시 2026-08-11) ──
    // 화면에 실제로 있는 치과만 고른다(연결 목록이 아니라 지금 일감이 있는 곳) — 건수는 세 구역을 합친 값.
    // 수집 카드는 파일에서 읽은 치과명이라 표기가 미세하게 다를 수 있어, 비교는 정규화 키로 한다.
    const clinicKeyOf = (row) => normKey(row._isParsed ? (row._clinic || '') : partnerName(row));
    const clinicNameOf = (row) => (row._isParsed ? (row._clinic || '') : partnerName(row)) || '';
    // (useMemo를 쓰지 않는다 — 이 지점은 조기 반환 뒤라 훅 순서가 깨진다. 목록이 작아 매 렌더 계산해도 무해)
    const clinicOptions = (() => {
        if (mode !== 'lab') return [];
        const m = new Map();
        [...freshRows, ...parsedActive, ...receivedOrders].forEach(r => {
            const k = clinicKeyOf(r);
            if (!k) return;
            const cur = m.get(k) || { key: k, name: clinicNameOf(r), n: 0 };
            cur.n++;
            m.set(k, cur);
        });
        return [...m.values()].sort((a, b) => b.n - a.n || a.name.localeCompare(b.name));
    })();
    // 저장된 치과 키가 지금 목록(clinicOptions)에 없으면 필터를 무시하고 전체를 보여 준다 (P1-2, 2026-08-12).
    // 빈 화면 방지 (P2-14 지적과 동일 원리). 저장값 자체는 지우지 않는다 — 스트림이 순차 도착하는 동안
    // 잠깐 목록에 없다고 기억을 지우면, 다음 렌더에 그 치과가 나타나도 필터가 풀려 있게 되기 때문.
    const clinicFilterEff = (clinicFilter && clinicOptions.some(c => c.key === clinicFilter)) ? clinicFilter : '';
    const byClinic = (rows) => (clinicFilterEff ? rows.filter(r => clinicKeyOf(r) === clinicFilterEff) : rows);
    const searchedOrders = byClinic(qOrders);
    const visParsed = byClinic(qParsed);
    // 접수 칸도 같은 검색어·같은 치과를 따른다 (모집단 자체는 본 보드와 분리 — active에 섞지 않음)
    const receivedVisible = byClinic(qReceived);
    // 신선도 분리 (P0): 30일 이내 = 기본 표시, 초과 = "오래된 접수" 접힘 (멈춤 판정·개수 배지 제외)
    const receivedFresh = receivedVisible.filter(isReceivedFresh);
    const receivedOld = receivedVisible.filter(o => !isReceivedFresh(o));

    // ── 납기 경과분 분리 (2026-08-07 대표 확정) ──
    // 납기 당일까지만 본 화면에 표시, 다음날부터 제외(오늘 마감 건은 당일 종일 표시).
    // 납기 없는 건은 기존대로 표시. 제외분은 사라지지 않고 하단 [지난 납기 N건 보기]로 펼침(조용한 소실 방지).
    const isPastDue = (o) => !!o._due && o._due.getTime() < sot;
    const visOrders = searchedOrders.filter(o => !isPastDue(o));
    const pastDueRows = searchedOrders.filter(isPastDue);
    // 수집 케이스 공정 칸 합류 (2026-09-03 대표 지시 "웹 의뢰서 없어도 감지되면 제작 공정에 뜨게" — 수기 의뢰 등):
    //  공정 신호가 있는(단계 미확인 아님) 수집 케이스는 웹 의뢰서와 같은 14일 신선도 컷으로 칸에 올린다.
    //  신호 없는 「단계 미확인」·오래된 건은 종전대로 ③ '의뢰서 없음' 접힘에 남긴다(조용한 소실 없음).
    const parsedForBoard = mode === 'lab'
        ? visParsed.filter(r => !r._stageUnknown && r._col !== null && !isPastDue(r) && (() => { const ms = stageSignalMs(r); return ms === null ? true : ms >= freshCutoff; })())
        : [];
    const parsedForBoardIds = new Set(parsedForBoard.map(r => r.id));
    // 보드(칸반) 열 안 카드도 정렬 적용 (대표 지시 2026-09-07 "순서는 날짜순") — 기본 납기 임박순, 선택 상자로 변경 가능
    const byCol = (key) => sortRows([...visOrders, ...parsedForBoard].filter(o => o._col === key));

    // ── 목록 보기: 행 정렬 (기본 납기 임박순 — 오늘→내일→이후, 납기 없음 뒤) ──
    const colIdxOf = (o) => viewColumns.findIndex(c => c[0] === o._col);
    const dueMs = (o) => (o._due ? o._due.getTime() : Infinity);
    const sortRows = (rows) => {
        if (sortKey === 'due') rows.sort((a, b) => dueMs(a) - dueMs(b));
        else if (sortKey === 'stage') rows.sort((a, b) => colIdxOf(a) - colIdxOf(b) || dueMs(a) - dueMs(b));
        else rows.sort((a, b) => tsToMs(b.stageUpdatedAt) - tsToMs(a.stageUpdatedAt));
        return rows;
    };
    // ① 제작 공정 = 웹 의뢰서 + 공정 신호 있는 수집 케이스 (2026-09-03 — 08-09 '웹 의뢰서만' 확정을 대표 지시로 변경)
    const listRows = sortRows([...visOrders, ...parsedForBoard]);
    const pastDueSorted = sortRows([...pastDueRows]);
    // ③ 의뢰서 없음 = 주문 없는 수집 케이스
    // 정렬 기준(대표 확정 2026-08-09): 파일 날짜(fileMtime) 최신순, 없으면 stageUpdatedAt 폴백 = stageSignalMs.
    //  · 종전 stageUpdatedAt 최신순은 '수집한 시각' 기준이라 2년 전 파일도 어제 수집하면 맨 위로 올라왔다.
    //  · 이 구역에는 날짜 컷을 걸지 않는다(청구 재료) — 오래된 건은 잘라내는 대신 아래로 내린다.
    //  · 시각을 못 읽은 건은 버리지 않고 맨 아래로 (조용한 소실 금지).
    const collectedMs = (r) => { const v = stageSignalMs(r); return v === null ? null : v; };
    const noOrderRows = mode === 'lab'
        ? [...visParsed].filter(r => !parsedForBoardIds.has(r.id)).sort((a, b) => {
            const ma = collectedMs(a), mb = collectedMs(b);
            if (ma === null && mb === null) return 0;
            if (ma === null) return 1;    // 판독 불가 → 아래로
            if (mb === null) return -1;
            return mb - ma;               // 최신순
        })
        : [];

    // ── 멈춤 요약 (2026-08-25 P1) — 본 보드(웹 의뢰서 신선분)+수집 카드 대상, 검색·치과 필터와 무관한 전체 기준.
    // 단계 미확인 수집분은 단계를 모르므로 제외 (모르는 것을 멈춤으로 세지 않는다).
    // 접수 칸(2026-09-01)의 received 멈춤(2일)은 기본 표시분(30일 이내)만 집계 — 접힘분(오래된 접수)은 제외.
    // caseChecks 판정 로직은 그대로 두고 표시층에서만 거른다.
    const stallTargets = mode === 'lab'
        ? [...freshRows, ...parsedActive.filter(r => !r._stageUnknown), ...receivedOrders.filter(isReceivedFresh)]
            .filter(o => o._col !== 'packed')   // 포장 완료(2026-10-12 3단계)는 멈춤 집계에서 뺀다 — 배지(stallBadgeEl)와 같은 기준
        : [];
    const stallInfos = stallTargets.map(o => getStallInfo(o));
    const stallYesterday = stallInfos.filter(s => (s.daysInStage || 0) >= 1).length;
    const stallOver = stallInfos.filter(s => s.stalled).length;

    // ── 덴클 꺼진 PC 판정 (24시간 이상 무신호 + 최근 30일 내 쓰던 활성 PC만 — 폐기 PC는 제외) ──
    const offlinePcs = mode === 'lab' ? agentDevices
        .map(d => ({ ...d, lastMs: tsToMs(d.lastSeen) }))
        .filter(d => d.lastMs > 0)
        .filter(d => {
            const off = Date.now() - d.lastMs;
            return off > 24 * 3600 * 1000 && off <= 30 * 24 * 3600 * 1000;
        })
        .map(d => ({
            name: deviceLabelOf(hwNames, d.deviceId, d.deviceName || 'PC'),
            days: Math.floor((Date.now() - d.lastMs) / 86400000),
        })) : [];

    // ── 공정 체크 보기 (기획서_QR공정체크 §5-2, 2026-08-18 착수) — 기공소 전용 ──
    // 저장된 'check' 보기가 치과 계정에 남아 있으면 목록으로 폴백 (혼선 방지)
    // 2026-10-25 대표 지시: 기공 현황은 캘린더만 — 공정 체크·보드·목록 보기 제거. 저장된 옛 보기값은 무시.
    //   포장 완료는 앱(Flutter lab_home_body → workOrders.checks.packing)과 서버 onOrderPackingDone 경로로만 기록되며 이 화면과 무관하게 유지된다.
    const effView = 'calendar';
    // 대상 = 본 보드(웹 의뢰서, 지난 납기 포함) + 파일 없음 의뢰서 + 수집 케이스 (중복은 기존 규칙이 이미 제거)
    const checkRows = (mode === 'lab' && effView === 'check') ? [
        ...freshRows.map(o => ({ id: 'o_' + o.id, kind: 'order', entity: o })),
        ...receivedOrders.map(o => ({ id: 'o_' + o.id, kind: 'order', entity: o })),
        ...parsedActive.map(r => ({ id: 'c_' + r.id, kind: 'case', entity: r })),
    ] : [];
    // 체크 저장 — 실패는 그대로 위로 던진다 (CaseCheckView가 받아 화면 롤백·사유 표시)
    const toggleCaseCheck = async (row, key, next) => {
        const patch = buildCheckPatch(key, next, row.entity);
        if (!patch) throw new Error('허용되지 않은 체크 항목입니다');
        const col = row.kind === 'case' ? 'parsedCases' : 'workOrders';
        await updateDoc(doc(db, col, row.entity.id), patch);
    };

    // ── 캘린더 보기 행 (2026-09-29 대표 확정 — docs/기획서_제작현황_캘린더뷰_2026-09-29.md) ──
    // 표시 대상 = 웹 의뢰서(workOrders)만. 수집 케이스(_isParsed·'pc_')와 치과 공유행(cc_·_fromShare)은 절대 올리지 않는다(결정 5).
    // 축은 납기(_due = getDueDate)뿐. 납기 없는 건은 캘린더가 개수만 표시한다.
    //  · 공정 행: searchedOrders(신선도 컷·검색·치과 필터 적용, 지난 납기 포함) 중 웹 의뢰서
    //  · 접수 행: 기공소=receivedVisible(receivedOrders 모집단, 검색·치과 필터 적용, 30일 초과분 포함) /
    //            치과=clinicReceivedRows(fresh+old)에 검색만 적용 — _col 'intake'로 '접수' 배지
    //  · 완료 행: processStage done(또는 낙관 완료 중) 웹 의뢰서 — [완료 포함] 체크 시만 캘린더가 회색으로 그린다
    // (useMemo를 쓰지 않는다 — 조기 반환 뒤라 훅 순서가 깨진다. clinicOptions와 같은 이유)
    const isWebRow = (o) => !o._isParsed && !o._fromShare;
    const calIntake = effView !== 'calendar' ? [] : (mode === 'lab'
        ? receivedVisible
        : (() => {
            const all = [...clinicReceivedRows.fresh, ...clinicReceivedRows.old];
            return qNorm ? all.filter(o => hitQ(o.patientName) || hitQ(partnerName(o))) : all;
        })()
    ).map(o => ({ ...o, _col: 'intake' }));
    const calRows = effView !== 'calendar' ? [] : [...searchedOrders.filter(isWebRow), ...calIntake];
    //  완료 판정(2026-09-29): done · 구 shipping(옛 4번째 열) · status completed(서버 자동 완료 포함) — 전부 회색 '완료'로만 보인다
    //  기공소(2026-10-12 3단계): 종결 행 배지는 '완료' 대신 stage3Of 라벨 — 사람이 종결한 건 = 포장 완료, 서버 자동 완료(auto_due)는
    //  남은 신호 단계(의뢰서 확인/디자인 완료) 또는 배지 없음(_badgeLabel ''). 회색·취소선(종결 표시)은 그대로.
    //  치과 모드(2026-10-12 3단계): clinicStageOf 라벨 — 사람이 종결 = '포장 완료'(상세 '기공소가 포장을 마침'), auto_due = '도착 예정일 지남'
    //  (상세 '자동 처리 — 도착 여부는 기공소와 확인'). '완료'·'받음'·'도착' 단정 없음.
    const calDoneRows = effView !== 'calendar' ? [] : byClinic(orders
        .filter(o => o.processStage === DONE || o.processStage === 'shipping' || o.status === 'completed' || pendingStages[o.id] === DONE)
        .filter(o => !['cancelled', 'canceled', 'rejected'].includes(o.status || ''))
        .filter(o => (qNorm ? (hitQ(o.patientName) || hitQ(partnerName(o))) : true))
        .map(o => ({
            ...o, _col: DONE, _due: getDueDate(o),
            ...(mode === 'lab'
                ? { _badgeLabel: stage3Of(o).label || '' }
                : (() => { const cs = clinicStageOf(o); return { _badgeLabel: cs.label || '', _detail: cs.detail || null }; })()),
        })));
    // 상단 띠 "지난 납기 미완료 N건" — 보드의 pastDueRows(같은 isPastDue 규칙) 웹 의뢰서 + 접수 행 중 지난 납기
    const calPastDue = effView !== 'calendar' ? [] : [...pastDueRows.filter(isWebRow), ...calIntake.filter(isPastDue)];
    // 멈춤 배지 — stallBadgeEl과 같은 조건(기공소만, 낙관 이동 중 카드는 생략), 판정은 getStallInfo 그대로
    // (구) 캘린더 멈춤 배지(calStallOf) — 2026-09-29 대표 지시로 캘린더에서는 멈춤 배지를 쓰지 않는다(보드·목록의 stallBadgeEl은 그대로)

    // 목록 납기 셀 (지남·오늘·내일 🔴)
    const eodMs = sot + 24 * 60 * 60 * 1000 - 1;
    const listDue = (o) => {
        if (!o._due) return <span style={{ color: C.muted }}>{'-'}</span>;
        const t = o._due.getTime();
        const md = `${o._due.getMonth() + 1}/${o._due.getDate()}`;
        let label = md, hot = false;
        if (t < sot) { label = `${md} 지남`; hot = true; }
        else if (t <= eodMs) { label = '오늘'; hot = true; }
        else if (t <= eot) { label = '내일'; hot = true; }
        return (
            <span style={{ color: hot ? C.critical : C.textSecondary, fontWeight: hot ? 700 : 600 }}>
                {hot ? '🔴 ' : ''}{label}
            </span>
        );
    };

    // 목록 진행 표시: ✓=지난 단계, ●=지금 단계, ·=미도달 — 완료(종결)는 목록에서 내려간다
    //   기공소·치과 공통 3단계(2026-10-12: 의뢰서 확인/디자인 완료/포장 완료 — 접수 칸은 단계 아님)
    const STEP_LABELS = LAB_COLUMNS.filter(c => c[0] !== 'intake').map(c => c[1]);
    const stepCell = (o) => {
        // 단계를 모르는 카드는 어느 칸도 완료로 칠하지 않는다 (검토 지적 P1-3, 2026-08-11).
        // 확인 안 된 것을 확인된 것처럼 보이면 안 된다 — 배지만 남긴다.
        if (o._stageUnknown) {
            return (
                <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                    {STEP_LABELS.map(lb => (
                        <span key={lb} style={{
                            fontSize: 11, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 3,
                            fontWeight: 600, color: C.muted,
                        }}>
                            <span>{'·'}</span>{lb}
                        </span>
                    ))}
                </div>
            );
        }
        // 열 순서에는 접수(intake)가 맨 앞에 있어 3단계 STEP_LABELS 기준으로 한 칸 당긴다(기공소·치과 공통)
        const ci = colIdxOf(o) - 1;
        return (
            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap' }}>
                {STEP_LABELS.map((lb, i) => {
                    const current = i === ci;
                    const done = i < ci;
                    return (
                        <span key={lb} style={{
                            fontSize: 11, whiteSpace: 'nowrap', display: 'inline-flex', alignItems: 'center', gap: 3,
                            fontWeight: current ? 700 : 600,
                            color: done ? C.good : current ? C.cat[0] : C.muted,
                        }}>
                            <span>{done ? '✓' : current ? '●' : '·'}</span>{lb}
                        </span>
                    );
                })}
            </div>
        );
    };

    // 수집일 (③구역 전용) — 수집 케이스에는 납기가 없으므로 파일 시각/단계 갱신 시각을 대신 보여 준다
    const collectedText = (o) => {
        const ms = (o.fileMtime ? new Date(o.fileMtime).getTime() : 0) || tsToMs(o.stageUpdatedAt);
        if (!ms || isNaN(ms)) return <span style={{ color: C.muted }}>{'-'}</span>;
        const d = new Date(ms);
        return <span style={{ color: C.textSecondary, fontWeight: 600 }}>{`${d.getMonth() + 1}/${d.getDate()}`}</span>;
    };

    // ── 목록 행 렌더 (기공소=이동 버튼, 치과=읽기 전용+기공소 배지) ──
    // asCollected=true(③ 의뢰서 없음 구역): 납기 칸을 수집일로 바꿔 표시. 그 밖의 동작(체크·단계 이동)은 동일.
    const renderListRow = (o, asCollected = false) => {
        const nextCol = nextColOf[o._col];
        const prevCol = prevColOf[o._col] || null;
        const late = mode === 'clinic' ? isOverdue(o) : (!o._isParsed && isDueSoon(o));
        return (
            <div key={(o._isParsed ? 'pc_' : '') + o.id} style={S.listRow}>
                <div style={{ ...S.patient, flex: '1.2 1 0', minWidth: 0 }}>
                    {late && <span style={S.urgentDot} />}
                    {mode === 'clinic' && (
                        <span style={S.badge(labAgentOk[o.toUserId] === false ? '#b6b4ad' : labColor(o.toUserId || ''))}>
                            {labInitial(partnerName(o))}
                        </span>
                    )}
                    <span style={{ overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                        {o._isParsed ? o._name : (o.patientName || '(환자명 없음)')}
                    </span>
                    {mode === 'clinic' && o._fromShare && <span style={S.parsedBadge}>{'수집'}</span>}
                    {o._isParsed && (
                        o._isScanOnly
                            ? <span style={S.scanBadge}>{'스캔'}</span>
                            : <span style={o._stageUnknown ? S.unknownBadge : S.parsedBadge}>{o._stageUnknown ? '단계 미확인' : '수집'}</span>
                    )}
                    {workDetailEl(o)}
                    {!o._stageUnknown && stallBadgeEl(o)}
                    {homeBadgeEl(o, true)}
                </div>
                <div style={{ color: C.muted, fontSize: 11.5, flex: '1 1 0', minWidth: 0, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>
                    {o._isParsed ? (o._clinic || (o._isScanOnly ? '' : '치과 미확인')) : partnerName(o)}
                </div>
                <div style={{ flex: '1.4 1 0', minWidth: 0 }}>{stepCell(o)}</div>
                {/* 밀링 단계 체크 셀 (기공소=체크박스, 치과=읽기 전용 배지) — 다른 단계는 빈 칸으로 정렬 유지 */}
                <div style={{ flex: '0 0 96px', display: 'flex', gap: 8, alignItems: 'center' }}>
                    {CHECK_COLS.includes(o._col) && (mode === 'lab'
                        ? MILL_CHECKS.map(([f, lb]) => {
                            const on = checkVal(o, f);
                            return (
                                <label key={f} style={S.checkLabel(on)}>
                                    <input type="checkbox" checked={on} style={S.checkBox}
                                        onChange={() => toggleCheck(o, f)} />
                                    {lb}
                                </label>
                            );
                        })
                        : millCheckBadges(o))}
                </div>
                <div style={{ flex: '0 0 76px', fontSize: 11 }}>{asCollected ? collectedText(o) : listDue(o)}</div>
                {mode === 'lab' && (
                    <div style={{ display: 'flex', gap: 5, flexShrink: 0 }}>
                        {prevCol && !revertBlocked(o, prevCol) && (
                            <button style={S.btn} disabled={movingId === o.id} title="이전 단계로"
                                onClick={() => moveStage(o, prevCol)}>{'◂'}</button>
                        )}
                        {prevCol && revertBlocked(o, prevCol) && (
                            <span style={{ ...S.sub, marginTop: 0, alignSelf: 'center' }} title={REVERT_BLOCKED_MSG}>{'되돌리기 불가'}</span>
                        )}
                        {nextCol && (
                            <button style={S.btnPrimary} disabled={movingId === o.id} title={nextLabelOf[o._col]}
                                onClick={() => moveStage(o, nextCol)}>{'다음 ▸'}</button>
                        )}
                    </div>
                )}
            </div>
        );
    };

    return (
        <div style={S.page}>
            {!embedded && (
                <>
                    <h1 style={S.h1}>기공 현황</h1>
                    <div style={S.statusline}>
                        <span style={S.dotLive} />
                        {mode === 'lab'
                            ? `실시간 · 의뢰서 접수 ${receivedFresh.length}건 · 제작 공정 ${listRows.length}건 · 의뢰서 없음 ${noOrderRows.length}건`
                            : `진행 중 ${allActive.length}건${clinicCollected.length ? ` (기공소 수집 ${clinicCollected.length}건 포함)` : ''}${scanIntakeOn ? ` · 구강스캔 ${clinicScanRows.fresh.length}건` : ''} · 의뢰서 접수 ${clinicReceivedRows.fresh.length}건`}
                    </div>
                </>
            )}

            {/* ── 확인할 것 요약 바 (2026-10-08 대표 지적 — 배너 4개가 겹쳐 쌓이던 것을 한 줄로 접음).
                 정보는 그대로: 접힌 상태에도 항목별 건수가 보이고, 펼치면 기존 문장·이동 링크·멈춤 기준표가 전부 나온다.
                 색은 가장 심각한 항목 1개만 적색(심각도: 수집 폴더 미지정 > 꺼진 PC > 멈춤 > 연결 안 된 반출). 항목 0개면 바 없음.
                 원래 배너: 수집 폴더 미지정(2026-08-09) / 덴클 꺼진 PC(2026-08-17 김레오니드 건) /
                 매칭 안 된 반출(기획서_작업현황_반출표시 §2-1, 조용한 소실 방지) / 멈춤 요약+기준표(2026-08-25 P1, 파생 표시·저장 없음) ── */}
            {!embedded && mode === 'lab' && (() => {
                const items = [];
                if (noFolderPcs.length > 0) items.push({
                    key: 'folder', short: `PC 설정 ${noFolderPcs.length}대`,
                    text: '⚠️ PC ' + noFolderPcs.length + '대에 수집 폴더가 지정되지 않았습니다 (' + noFolderPcs.join(', ') + ') — 그 PC에서 작업한 케이스는 올라오지 않습니다.',
                    link: '설정에서 확인 ›', to: '/settings/devices', title: '설정 > 접속 PC 관리로 이동',
                });
                if (offlinePcs.length > 0) items.push({
                    key: 'offline', short: `꺼진 PC ${offlinePcs.length}대`,
                    text: '⚠️ 덴클 프로그램이 꺼진 PC ' + offlinePcs.length + '대: '
                        + offlinePcs.map(p => `${p.name}(${p.days}일째)`).join(', ')
                        + ' — 이 PC에서 한 작업(밀링·수집)은 다시 켤 때까지 공정에 반영되지 않습니다.',
                    link: 'PC 관리 ›', to: '/settings/devices', title: '설정 > 접속 PC 관리로 이동',
                });
                if (stallYesterday > 0 || stallOver > 0) items.push({
                    key: 'stall', short: stallOver > 0 ? `멈춤 ${stallOver}건` : `미완료 ${stallYesterday}건`,
                    text: `⚠ 어제까지 들어온 것 중 미완료 ${stallYesterday}건 · 기준 초과 멈춤 ${stallOver}건`,
                    stall: true,
                });
                if (homeUnmatched > 0) items.push({
                    key: 'home', short: `연결 안 된 케이스 ${homeUnmatched}건`,
                    text: `🏠 다른 PC에 나가 있는 케이스 ${homeUnmatched}건은 폴더 이름이 달라 이 목록과 연결하지 못했습니다.`,
                    link: '재택근무 화면에서 확인 ›', to: '/homework', title: '재택근무 화면으로 이동',
                });
                if (items.length === 0) return null;
                const topKey = items[0].key;   // 위 push 순서 = 심각도 순서 → 첫 항목만 강조
                return (
                    <div style={S.alertWrap}>
                        <button type="button" style={S.alertBar} onClick={toggleAlertsOpen} aria-expanded={alertsOpen}>
                            <span style={{ display: 'flex', alignItems: 'center', gap: 6, flexWrap: 'wrap', minWidth: 0 }}>
                                <span style={{ fontWeight: 800, color: C.textPrimary }}>{`확인할 것 ${items.length}가지`}</span>
                                {items.map(it => (
                                    <span key={it.key} style={it.key === topKey ? S.alertChipTop : S.alertChip}>{it.short}</span>
                                ))}
                            </span>
                            <span style={S.alertToggle}>{alertsOpen ? '접기 ▴' : '자세히 ▾'}</span>
                        </button>
                        {alertsOpen && (
                            <div style={S.alertList}>
                                {items.map(it => (
                                    <div key={it.key} style={S.alertRow}>
                                        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 8, flexWrap: 'wrap' }}>
                                            <span style={{ color: it.key === topKey ? C.critical : C.textSecondary, fontWeight: 700, minWidth: 0 }}>{it.text}</span>
                                            {it.to && (
                                                <button type="button" style={S.alertLink} onClick={() => navigate(it.to)} title={it.title}>{it.link}</button>
                                            )}
                                            {it.stall && (
                                                <button type="button" style={S.alertLink} onClick={() => setShowStallGuide(v => !v)}>
                                                    {showStallGuide ? '멈춤 기준 닫기 ▴' : '멈춤 기준 보기 ▾'}
                                                </button>
                                            )}
                                        </div>
                                        {it.stall && showStallGuide && (
                                            <div style={S.stallGuideBox}>
                                                <div style={{ fontWeight: 700, color: C.textPrimary, marginBottom: 6 }}>
                                                    {'멈춤 판정 기준 — 화면 표시용 계산이며 어디에도 저장되지 않습니다'}
                                                </div>
                                                <div style={{ display: 'flex', gap: 14, flexWrap: 'wrap', fontWeight: 700 }}>
                                                    {/* 4단계 기준표 전부 표시 (utils/caseChecks.js STALL_STAGES — 접수/확인/디자인완료/밀링완료) */}
                                                    {STALL_STAGES.map(s => <span key={s.stage}>{`${s.label} ${s.days}일`}</span>)}
                                                </div>
                                                <div style={{ marginTop: 6, color: C.muted }}>
                                                    {`내원일까지 여유가 ${STALL_VISIT_SLACK_DAYS}일 미만이면 모든 단계를 1일로 조입니다. 내원일이 없으면 위 기본값을 씁니다. 단계에 들어간 시각을 못 읽는 케이스는 멈춤으로 세지 않습니다.`}
                                                </div>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        )}
                    </div>
                );
            })()}

            {/* ── 대시보드 임베드: 검색만 노출 (2026-09-03 대표 요청 "대시보드 제작현황에도 환자이름 검색") ── */}
            {embedded && (
                <div style={{ ...S.searchWrap, maxWidth: 320, marginBottom: 10 }}>
                    <input style={S.searchInput} value={qText} onChange={(e) => setQText(e.target.value)}
                        placeholder="환자명·치과명 검색" aria-label="기공 현황 검색" />
                    {qText && <button style={S.searchClear} onClick={() => setQText('')} title="지우기">×</button>}
                </div>
            )}
            {/* ── 검색 + 보기 전환 툴바 (embedded 대시보드는 캘린더 고정) ── */}
            {!embedded && (
                <div style={S.toolbar}>
                    {effView !== 'check' && (
                        <div style={S.searchWrap}>
                            <input
                                style={S.searchInput}
                                value={qText}
                                onChange={(e) => setQText(e.target.value)}
                                placeholder={mode === 'lab' ? '환자명·치과명 검색' : '환자명·기공소명 검색'}
                            />
                            {qText && (
                                <button style={S.searchClear} onClick={() => setQText('')} title="지우기">{'×'}</button>
                            )}
                        </div>
                    )}
                    <div style={{ display: 'flex', gap: 6, alignItems: 'center', flexWrap: 'wrap' }}>
                        {mode === 'lab' && effView !== 'check' && clinicOptions.length > 0 && (
                            <select style={S.sortSel} value={clinicFilterEff} onChange={(e) => setClinicFilterPersist(e.target.value)} title="치과별 보기">
                                <option value="">전체 치과</option>
                                {clinicOptions.map(c => (
                                    <option key={c.key} value={c.key}>{`${c.name} (${c.n})`}</option>
                                ))}
                            </select>
                        )}
                        {mode === 'lab' && effView !== 'check' && effView !== 'calendar' && (
                            <button style={S.toggleBtn} onClick={() => {
                                const v = !hideUnknown; setHideUnknown(v);
                                try { localStorage.setItem('prodHideUnknown', v ? '1' : '0'); } catch (e) { /* 무시 */ }
                            }}>{hideUnknown ? '미확인 보이기' : '미확인 숨기기'}</button>
                        )}
                        {(effView === 'list' || effView === 'board') && (
                            <select style={S.sortSel} value={sortKey} onChange={(e) => setSortKeyPersist(e.target.value)}>
                                <option value="due">납기 임박순</option>
                                <option value="stage">단계순</option>
                                <option value="updated">최근 갱신순</option>
                            </select>
                        )}
                        {mode === 'clinic' && (
                            <button style={S.toggleBtn} onClick={loadClinicShared} disabled={clinicSharedBusy}>
                                {clinicSharedBusy ? '불러오는 중…' : '↻ 새로 고침'}
                            </button>
                        )}
                        {/* 2026-10-25 대표 지시: [✓ 공정 체크] 버튼과 보드/목록/캘린더 전환 제거 — 캘린더만. 복원용 종전 코드는 git 없음 → docs/LEDGER 10-25 항목 참조 */}
                    </div>
                </div>
            )}

            {/* ── 보드 헤드 (치과: 기공소 범례 — embedded에서는 섹션 제목이 밖에 있으므로 보드 제목 숨김) ── */}
            <div style={S.boardHead}>
                {!embedded && <div style={S.boardTitle}>{effView === 'calendar'
                    ? `납기 캘린더 ${calRows.filter(o => o._due).length}건`
                    : mode === 'lab'
                        ? (effView === 'check' ? `공정 체크 ${checkRows.length}건` : `제작 공정 ${listRows.length}건`)
                        : '우리 주문 진행 현황 (통합 보드)'}</div>}
                {/* 🦷 기공소 수집 케이스 공개 상태 안내 (치과 모드) — 조용한 실패 금지: 실패 사유도 보인다 */}
                {!embedded && mode === 'clinic' && clinicShared.loaded && (
                    clinicShared.error
                        ? <div style={{ fontSize: 11.5, color: C.critical, marginBottom: 6 }}>{'기공소 수집 케이스를 불러오지 못했습니다: ' + clinicShared.error}</div>
                        : (clinicShared.labs.length > 0 && clinicShared.labs.every(l => !l.enabled) && (
                            <div style={{ fontSize: 11.5, color: C.muted, marginBottom: 6 }}>
                                {'기공소가 아직 공정 공개를 켜지 않았습니다 — 여기에는 이 치과가 웹으로 보낸 의뢰서 진행만 표시됩니다.'}
                            </div>
                        ))
                )}
                {mode === 'clinic' && (
                    <div style={S.legend}>
                        {labList.map(l => {
                            const ok = labAgentOk[l.labId];
                            return (
                                <span key={l.labId || l.name} style={S.legendItem}>
                                    <span style={S.badge(ok === false ? '#b6b4ad' : labColor(l.labId))}>{labInitial(l.name)}</span>
                                    {l.name}
                                    {ok === false && <span style={{ color: C.muted }}>{'· 연결 전 (에이전트 미가동)'}</span>}
                                    {ok === null && <span style={{ color: C.muted }}>{'· 연결 확인 불가'}</span>}
                                </span>
                            );
                        })}
                    </div>
                )}
            </div>

            {(!embedded && effView === 'check' && mode === 'lab') ? (
                /* ── 공정 체크 보기 (2026-08-18) — 검색·칩·인라인 체크. 저장 실패는 화면에서 롤백된다 ── */
                <CaseCheckBoard rows={checkRows} onToggle={toggleCaseCheck} />
            ) : (effView === 'calendar') ? (   /* embedded 대시보드도 캘린더 (2026-09-29) */
                /* ── 캘린더 보기 (2026-09-29 대표 확정) — 납기일 축, 웹 의뢰서만. 쓰기는 보드 핸들러 그대로 주입 ── */
                <ProductionCalendar
                    mode={mode}
                    connError={mode === 'lab' ? connError : null}
                    rows={calRows}
                    doneRows={calDoneRows}
                    pastDueRows={calPastDue}
                    columns={viewColumns}
                    partnerNameOf={partnerName}
                    checkVal={checkVal}
                    millChecks={MILL_CHECKS}
                    nextColOf={nextColOf}
                    prevColOf={prevColOf}
                    revertBlocked={revertBlocked}
                    revertBlockedMsg={REVERT_BLOCKED_MSG}
                    nextLabelOf={nextLabelOf}
                    movingId={movingId}
                    onOpen={(o) => navigate(`/view-order/${o.id}`)}
                    onMoveStage={moveStage}
                    onIntakeToConfirm={moveIntakeToConfirm}
                    onToggleCheck={toggleCheck}
                    onPreview3d={openDesignPreview}
                    escDisabled={!!stlPreview}
                />
            ) : (embedded || effView === 'board') ? (
                /* ── 보드 — 기공소 3단계(2026-10-12: 의뢰서 접수 칸 + 의뢰서 확인 / 디자인 완료 / 포장 완료) · 치과 종전 4단계 — 완료(종결)는 서버 자동, 보드에서 내려감 ── */
                <div style={S.board}>
                    {/* ── 접수 칸 (기공소 — 구 ②'파일 없음' 구역을 흡수, 모집단 동일·이중 표시 방지) ── */}
                    {mode === 'lab' && (
                        <div style={S.col}>
                            <div style={S.colHead}>
                                <span>{'의뢰서 접수'} <span style={S.colCount}>{receivedFresh.length}</span></span>
                            </div>
                            <div style={S.colCaption}>{'아직 열어 보지 않은 의뢰서'}</div>
                            <div style={S.cards}>
                                {connError && (
                                    <div style={{ ...S.empty, color: C.critical }}>
                                        {'접수 목록을 확인하지 못했습니다 (거래처 목록 조회 실패: ' + connError + ')'}
                                    </div>
                                )}
                                {!connError && receivedFresh.length === 0 && receivedOld.length === 0 && <div style={S.empty}>없음</div>}
                                {receivedFresh.map(o => renderReceivedCard(o, false))}
                                {receivedOld.length > 0 && (
                                    <button style={S.oldFoldBar} onClick={() => setShowOldReceived(v => !v)}
                                        title={`받은 지 ${RECEIVED_FRESH_DAYS}일이 지난 접수입니다 — 눌러서 펼치기`}>
                                        {(showOldReceived ? '▾ ' : '▸ ') + '오래된 접수 ' + receivedOld.length + '건'}
                                    </button>
                                )}
                                {showOldReceived && receivedOld.map(o => renderReceivedCard(o, true))}
                                {renderHiddenReceivedFold()}
                            </div>
                        </div>
                    )}
                    {/* ── 치과 2칸 (2026-09-01 사양 B): 구강스캔 감지 → 의뢰서 접수 → 기존 공정 칸 ──
                         전이는 데이터 필터만으로 성립: 스캔에 의뢰서가 연결되면(orderId 생김) 감지 칸에서 빠져
                         접수 칸으로, 기공소 첫 공정 신호가 오면 접수 칸에서 빠져 기존 공정 칸으로 간다. */}
                    {mode === 'clinic' && (
                        /* 의뢰서 접수 칸(치과) — 2026-09-04: 구 '구강스캔' 칸을 이 칸 안 점선 카드로 접었다.
                           스캔 카드 = clinicScanIntake 플래그 게이트(검수 P1, CreateOrder와 동일 기준), 의뢰서 카드는 플래그와 무관.
                           전이는 데이터 필터만으로 성립: 스캔에 의뢰서가 연결되면(orderId) 점선 카드가 의뢰서 카드가 되고,
                           기공소가 열어 보면(processStage received) 확인 칸으로 간다. */
                        <div style={S.col}>
                            <div style={S.colHead}>
                                <span>{'의뢰서 접수'} <span style={S.colCount}>{clinicReceivedRows.fresh.length + (scanIntakeOn ? clinicScanRows.fresh.length : 0)}</span></span>
                            </div>
                            <div style={S.colCaption}>
                                {scanIntakeOn ? '보낸 의뢰서(기공소 확인 전) · 점선 = 의뢰서를 아직 안 보낸 스캔' : '보낸 의뢰서 — 기공소 확인 전'}
                                {scanIntakeOn && scansCapped && ` · 스캔은 최근 ${SCAN_LIMIT}건까지`}
                            </div>
                            <div style={S.cards}>
                                {clinicReceivedRows.fresh.length === 0 && clinicReceivedRows.old.length === 0
                                    && (!scanIntakeOn || (clinicScanRows.fresh.length === 0 && clinicScanRows.old.length === 0))
                                    && <div style={S.empty}>없음</div>}
                                {clinicReceivedRows.fresh.map(renderClinicReceivedCard)}
                                {scanIntakeOn && clinicScanRows.fresh.map(renderScanCard)}
                                {clinicReceivedRows.old.length > 0 && (
                                    <button style={S.oldFoldBar} onClick={() => setShowOldClinicReceived(v => !v)}
                                        title={`보낸 지 ${RECEIVED_FRESH_DAYS}일이 지난 의뢰서입니다 — 눌러서 펼치기`}>
                                        {(showOldClinicReceived ? '▾ ' : '▸ ') + '오래된 접수 ' + clinicReceivedRows.old.length + '건'}
                                    </button>
                                )}
                                {showOldClinicReceived && clinicReceivedRows.old.map(renderClinicReceivedCard)}
                                {scanIntakeOn && clinicScanRows.old.length > 0 && (
                                    <button style={S.oldFoldBar} onClick={() => setShowOldScans(v => !v)}
                                        title={`스캔한 지 ${SCAN_FRESH_DAYS}일이 지난 스캔입니다 — 눌러서 펼치기`}>
                                        {(showOldScans ? '▾ ' : '▸ ') + '오래된 스캔 ' + clinicScanRows.old.length + '건'}
                                    </button>
                                )}
                                {scanIntakeOn && showOldScans && clinicScanRows.old.map(renderScanCard)}
                            </div>
                        </div>
                    )}
                    {viewStageColumns.map(([key, label]) => {
                        // 공정 3열(기공소: 의뢰서 확인/디자인 완료/포장 완료 · 치과: 확인/디자인완료/밀링완료) — 의뢰서 접수 칸은 위에서 따로 그렸다.
                        // 웹 의뢰서 + 공정 신호 있는 수집 케이스(parsedForBoard, 2026-09-03). 단계 미확인 수집분은 ③ '의뢰서 없음'.
                        const cards = byCol(key);
                        return (
                            <div key={key} style={S.col}>
                                <div style={S.colHead}>
                                    <span>{label} <span style={S.colCount}>{cards.length}</span></span>
                                </div>
                                <div style={S.cards}>
                                    {cards.length === 0 && <div style={S.empty}>없음</div>}
                                    {cards.map(o => (mode === 'lab' ? renderLabCard(o) : renderClinicCard(o)))}
                                </div>
                            </div>
                        );
                    })}
                </div>
            ) : (
                /* ── 목록 보기 (기본, 2026-08-07 대표 요청) ── */
                <div style={S.listWrap}>
                    {listRows.length === 0 && (
                        <div style={S.empty}>{qNorm ? '검색 결과가 없습니다' : '진행 중인 케이스가 없습니다'}</div>
                    )}
                    {listRows.map(renderListRow)}
                </div>
            )}

            {/* ── ①구역 신선도 안내 (대표 확정 2026-08-09) — 14일이 지나 빠진 건이 조용히 사라지지 않도록 항상 한 줄 표기 ── */}
            <div style={{ marginTop: 8, fontSize: 11.5, color: C.muted }}>
                {`제작 공정에는 최근 ${STAGE_FRESH_DAYS}일 이내 파일만 표시합니다.`}
                {staleHidden > 0 && ` 그보다 오래된 ${staleHidden}건은 여기서 내렸습니다(의뢰서·수집함 목록에는 그대로 있습니다).`}
            </div>

            {/* ── 지난 납기 접이식 (납기 다음날부터 본 화면에서 빠진 건 — 조용한 소실 방지, 0건이면 미표시)
                 공정 체크 보기에서는 지난 납기·파일 없음·수집 케이스가 이미 본 목록에 합쳐져 있어 아래 구역들을 숨긴다 ── */}
            {effView !== 'check' && effView !== 'calendar' && pastDueSorted.length > 0 && (
                <div style={{ marginTop: 12 }}>
                    <button style={S.pastDueBar} onClick={() => setShowPastDue(v => !v)}>
                        <span>{(showPastDue ? '▾ ' : '▸ ') + '지난 납기 ' + pastDueSorted.length + '건 보기'}</span>
                        <span style={{ color: C.muted, fontWeight: 600 }}>{'납기일이 지나 위 목록에서 내려간 케이스입니다'}</span>
                    </button>
                    {showPastDue && (
                        <div style={{ ...S.listWrap, marginTop: 6 }}>
                            {pastDueSorted.map(renderListRow)}
                        </div>
                    )}
                </div>
            )}

            {/* ── (구) ② '파일 없음' 구역 (W1-1, 2026-08-09) — 2026-09-01 접수 칸으로 흡수됨.
                 모집단이 접수 칸과 동일해 이중 표시 방지를 위해 별도 목록을 제거했다. 원 렌더는 아래 주석 보존.
            {mode === 'lab' && !embedded && effView !== 'check' && (connError || receivedVisible.length > 0) && (
                <div style={{ marginTop: 12 }}>
                    {connError ? (
                        <div style={{ ...S.noFileBar, cursor: 'default' }}>
                            <span>{'파일 없는 의뢰서를 확인하지 못했습니다'}</span>
                            <span style={{ color: C.muted, fontWeight: 600 }}>{'거래처 목록 조회 실패: ' + connError}</span>
                        </div>
                    ) : (
                        <>
                            <button style={S.noFileBar} onClick={() => setShowNoFile(v => !v)}>
                                <span>{(showNoFile ? '▾ ' : '▸ ') + '파일 없음 ' + receivedVisible.length + '건'}</span>
                                <span style={{ color: C.muted, fontWeight: 600 }}>{'아직 CAD 파일이 연결되지 않은 의뢰서입니다'}</span>
                            </button>
                            {showNoFile && (
                                <div style={{ ...S.listWrap, marginTop: 6 }}>
                                    ...행 렌더(renderReceivedRow의 원형 — 환자명/치과/납기/경과일)...
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}
            ── 원 렌더 주석 끝 ── */}

            {/* ── 접수 (기공소 · 목록 보기 전용, 2026-09-01) — 보드 보기에서는 접수 칸이 대신 보여 준다(이중 표시 방지) ── */}
            {mode === 'lab' && !embedded && effView === 'list' && (connError || receivedVisible.length > 0) && (
                <div style={{ marginTop: 12 }}>
                    {connError ? (
                        <div style={{ ...S.noFileBar, cursor: 'default' }}>
                            <span>{'접수 의뢰서를 확인하지 못했습니다'}</span>
                            <span style={{ color: C.muted, fontWeight: 600 }}>{'거래처 목록 조회 실패: ' + connError}</span>
                        </div>
                    ) : (
                        <>
                            <button style={S.noFileBar} onClick={() => setShowNoFile(v => !v)}>
                                <span>{(showNoFile ? '▾ ' : '▸ ') + '의뢰서 접수 ' + receivedFresh.length + '건'}</span>
                                <span style={{ color: C.muted, fontWeight: 600 }}>{'아직 열어 보지 않은 의뢰서입니다'}</span>
                            </button>
                            {showNoFile && (
                                <div style={{ ...S.listWrap, marginTop: 6 }}>
                                    <div style={{ fontSize: 11.5, color: C.muted, padding: '2px 2px 4px' }}>
                                        {`최근 ${ORDER_WINDOW_DAYS}일 안에 받은 의뢰서만 셉니다. 의뢰서를 열어 보면 자동으로 [의뢰서 확인]으로 옮겨지고, [▸ 의뢰서 확인으로] 버튼으로 직접 옮길 수도 있습니다.`}
                                    </div>
                                    {receivedFresh.map(o => renderReceivedRow(o, false))}
                                    {receivedOld.length > 0 && (
                                        <button style={S.oldFoldBar} onClick={() => setShowOldReceived(v => !v)}>
                                            {(showOldReceived ? '▾ ' : '▸ ') + '오래된 접수 ' + receivedOld.length + '건'}
                                        </button>
                                    )}
                                    {showOldReceived && receivedOld.map(o => renderReceivedRow(o, true))}
                                    {renderHiddenReceivedFold()}
                                </div>
                            )}
                        </>
                    )}
                </div>
            )}

            {/* ── 🦷 치과 · 목록 보기 전용 (2026-09-01) — 보드 보기에서는 구강스캔/의뢰서 접수 칸이 대신 보여 준다 ── */}
            {mode === 'clinic' && !embedded && effView === 'list'
                && (clinicScanRows.fresh.length + clinicScanRows.old.length + clinicReceivedRows.fresh.length + clinicReceivedRows.old.length) > 0 && (
                <div style={{ marginTop: 12, display: 'flex', flexDirection: 'column', gap: 8 }}>
                    {/* 구강스캔 구역 — clinicScanIntake 플래그 게이트 (검수 P1). OFF면 구독이 안 돌아 목록도 비지만 이중 방어 */}
                    {scanIntakeOn && (clinicScanRows.fresh.length + clinicScanRows.old.length) > 0 && (
                        <div>
                            <div style={{ fontSize: 12.5, fontWeight: 700, color: C.textSecondary, marginBottom: 6 }}>
                                {`구강스캔 ${clinicScanRows.fresh.length}건 — 의뢰서를 아직 안 보낸 스캔`}
                                {scansCapped && <span style={{ color: C.muted, fontWeight: 600 }}>{` · 최근 ${SCAN_LIMIT}건까지 표시`}</span>}
                            </div>
                            <div style={S.listWrap}>
                                {clinicScanRows.fresh.map(renderScanCard)}
                                {clinicScanRows.old.length > 0 && (
                                    <button style={S.oldFoldBar} onClick={() => setShowOldScans(v => !v)}>
                                        {(showOldScans ? '▾ ' : '▸ ') + '오래된 스캔 ' + clinicScanRows.old.length + '건'}
                                    </button>
                                )}
                                {showOldScans && clinicScanRows.old.map(renderScanCard)}
                            </div>
                        </div>
                    )}
                    {(clinicReceivedRows.fresh.length + clinicReceivedRows.old.length) > 0 && (
                        <div>
                            <div style={{ fontSize: 12.5, fontWeight: 700, color: C.textSecondary, marginBottom: 6 }}>
                                {`의뢰서 접수 ${clinicReceivedRows.fresh.length}건 — 기공소 확인 전`}
                            </div>
                            <div style={S.listWrap}>
                                {clinicReceivedRows.fresh.map(renderClinicReceivedCard)}
                                {clinicReceivedRows.old.length > 0 && (
                                    <button style={S.oldFoldBar} onClick={() => setShowOldClinicReceived(v => !v)}>
                                        {(showOldClinicReceived ? '▾ ' : '▸ ') + '오래된 접수 ' + clinicReceivedRows.old.length + '건'}
                                    </button>
                                )}
                                {showOldClinicReceived && clinicReceivedRows.old.map(renderClinicReceivedCard)}
                            </div>
                        </div>
                    )}
                </div>
            )}

            {/* ── ③ 의뢰서 없음 (수집 케이스만 있는 건) — 기공소 전용·기본 접힘 ── */}
            {/* 데이터는 그대로 유지한다: 거래명세서 청구 재료라 조회·집계에서 빼지 않고 화면 배치만 옮긴 것. */}
            {/* 캘린더 보기에서는 숨김 (2026-09-29: 캘린더는 웹 의뢰서만 — 수집 케이스 구역이 섞여 보이지 않게) */}
            {mode === 'lab' && effView !== 'check' && effView !== 'calendar' && noOrderRows.length > 0 && (
                <div style={{ marginTop: 12 }}>
                    <button style={S.noOrderBar} onClick={() => setShowNoOrder(v => !v)}>
                        <span>{(showNoOrder ? '▾ ' : '▸ ') + '의뢰서 없음 ' + noOrderRows.length + '건'}</span>
                        <span style={{ color: C.muted, fontWeight: 600 }}>{'수집된 파일만 있고 웹 의뢰서와 아직 연결되지 않은 케이스입니다'}</span>
                    </button>
                    {showNoOrder && (
                        <div style={{ ...S.listWrap, marginTop: 6 }}>
                            <div style={{ fontSize: 11.5, color: C.muted, padding: '2px 2px 4px' }}>
                                {'환자명·치과명은 파일에서 읽은 값이라 실제와 다를 수 있습니다. 거래명세서 작성에는 그대로 쓰입니다.'}
                            </div>
                            {noOrderRows.map(r => renderListRow(r, true))}
                        </div>
                    )}
                </div>
            )}

            {/* ── 표시 한도 안내 (조회 창을 꽉 채운 경우만 — 조용한 누락 방지) ── */}
            {(ordersCapped || parsedCapped) && (
                <div style={{ marginTop: 12, fontSize: 12, color: C.textSecondary, background: '#fdfaf0', border: '1px solid rgba(237,161,0,0.35)', borderRadius: 8, padding: '9px 12px' }}>
                    {`조회 한도(최근 ${ORDER_WINDOW_DAYS}일·최신순)에 도달했습니다. 같은 기간 안에서도 아주 오래된 건은 불러오지 못했을 수 있습니다. (의뢰서·수집함 목록에는 그대로 남아 있습니다)`}
                </div>
            )}

            {mode === 'lab' && effView !== 'calendar' && (
                <div style={{ marginTop: 14, fontSize: 12, color: C.textSecondary, background: C.surface, border: `1px solid ${C.border}`, borderRadius: 8, padding: '9px 12px' }}>
                    {'아직 열어 보지 않은 의뢰서는 [의뢰서 접수] 칸에, 의뢰서 없이 파일만 수집된 케이스는 [의뢰서 없음] 줄에 모아 두었습니다(거래명세서에는 그대로 쓰입니다). 의뢰서를 열어 보면 [의뢰서 확인]으로, 디자인이 끝나거나 밀링이 감지되면 [디자인 완료]로 덴클 프로그램이 자동으로 옮깁니다. [의뢰서 확인]과 [디자인 완료] 사이는 버튼으로 직접 옮길 수도 있습니다. 공정 체크에서 포장 완료를 체크하면 [포장 완료]로 옮겨집니다. 납기일이 지나면 자동으로 완료 처리되어 이 화면에서 내려갑니다(완료 표시는 편의 표시이며 납품·수령을 증명하지 않습니다).'}
                </div>
            )}

            {/* 캘린더 [3D 디자인 확인] 뷰어 — 보철물만 표시(스캔 모델 제외, StlViewer가 거른다) */}
            {stlPreview && (
                <StlViewer entries={stlPreview.entries} title={stlPreview.title} notice={stlPreview.notice} onClose={() => setStlPreview(null)} />
            )}
        </div>
    );
}

export default ProductionBoard;
